/*
  Large Custom Stencils configurator — the <pricer-lg-app> custom element.

  WHERE THIS CAME FROM: Colin's prototype large-pricer-preview.html (emailed
  2026-09-04, "Pricer for Large Custom Stencils"). The prototype supplied the
  interaction layer — four steps, Upload → Size → Sheets → Review, with
  quantity moved onto Review because ~95% of live large orders are qty 1.
  Everything BELOW the interaction layer is ported from assets/pricer-v2.js,
  which is what Colin was asking about when he wrote "the back end should come
  through with a crop same as the mylar one. Not sure if that code is in here."
  It was not in the prototype. It is in here.

  THE CUSTOM ELEMENT IS <pricer-lg-app>, NOT <pricer-app-v2>, and the CSS is
  scoped to it. This is the whole safety story for the file, and it is the same
  reasoning as pricer-qr.js (CHANGELOG 2026-08-29): the large, mylar and QR
  configurators share EVERY class name, and pricer-v2.js bails out early if
  pricer-app-v2 is already defined. Reusing the tag would make a large-stencil
  bug reachable from Custom Mylar, which is a product actually taking orders.

  PORTED UNCHANGED FROM pricer-v2.js — every one of these was found the hard way
  against the live store, so read the header of that file before touching any:
    - handleAddToCart's variant-propagation retry loop (six attempts, 1s
      doubling to 5s). A variant created moments earlier through the Admin API
      can still be invisible to the Storefront cart.
    - the 45s failsafe that re-enables the button.
    - _artworkReady — Add to Cart awaits any in-flight cropped-render rebuild.
    - the 20MB line-item-property cap, surfaced at the upload control.
    - pdf.js for PDF and .ai, loaded lazily on first use.
    - the no-preview acknowledgement gate for files we can't rasterise.
    - refreshCroppedUpload / buildDesignerSvg — the crop as a picture, and the
      Illustrator file whose CROP-FRAME--DO-NOT-PRINT layer measures the
      ordered size on the artboard.
    - sizeCropImage / reflowCrop — the crop-stage floor and the resize
      reprojection (CHANGELOG 2026-08-24).

  NEW HERE, with no mylar equivalent:
    - the Sheets step: tiling the design across standard or jumbo sheets,
      drawing the split, and putting the result on the order.
    - the inches/feet unit toggle on the Size step.
    - the tile map SVG attached for the production floor.

  PRICING: assets/pricer-large-data.js. IT IS A PLACEHOLDER AND SAYS SO. While
  `placeholder` is set the configurator runs and shows prices so the flow can be
  tested, but Add to Cart stays disabled and says why — see refreshGate(). The
  backend calculator also refuses anything over 48" today, so there is no path
  to a mispriced order; this just makes the refusal legible instead of a failed
  fetch at the last step.

  PRICING BACKEND: identical to mylar — Add to Cart calls the
  stencil-pricer-backend app's /apps/pricer/quote endpoint through Shopify's app
  proxy to mint a variant priced exactly right, then submits THAT variant. That
  is what makes charged match shown.
*/
(function () {
  if (customElements.get('pricer-lg-app')) return;

  var DATA = window.PRICER_LARGE_DATA;

  /* Everything physical and every number comes from the data file, so the real
     pricing table can land without touching this file. The fallbacks exist only
     so a missing data file degrades to a working UI with no price rather than a
     stack trace on a customer's phone — refreshGate() blocks the sale either
     way. */
  var MIN_DIM   = (DATA && DATA.minDim)   || 2;     // per side; was 12 until 2026-10-15
  var MAX_DIM   = (DATA && DATA.maxDim)   || 360;   // 30 ft
  var MAX_QTY   = (DATA && DATA.maxQty)   || 999;
  var SHEETS    = (DATA && DATA.sheets)   || [];
  var PRESETS   = (DATA && DATA.presets)  || [];

  /* Colin's real merged pricing table (see the PRICING block below). Held as
     nulls rather than empty arrays when absent, so a lookup returns null — "no
     price" — instead of quietly reading off the end of an empty table. */
  var SIZE_AXIS   = (DATA && DATA.sizeAxis)   || null;
  var SIZE_ADDERS = (DATA && DATA.sizeAdders) || null;
  var AREA_ROWS   = (DATA && DATA.areaRows)   || null;
  var QTY_COLS    = (DATA && DATA.qtyCols)    || null;
  var QTY_ADDERS  = (DATA && DATA.qtyAdders)  || null;

  /* Where "Need something bigger? Contact us" points, once the customer hits the
     30 ft ceiling. Overridable from the snippet via data-contact-url so the path
     is not hardcoded in a JS asset if the page ever moves. */
  var CONTACT_URL = '/pages/contact';
  /* A size Mylar can take (both sides up to 36") points here (Colin,
     2026-10-09: "make sure there's a link that kicks over to custom mylar
     below the 12 inch size"; the 12" floor became the 36" rule on 10-15). */
  var MYLAR_URL = '/products/custom-mylar-stencils';
  /* Custom Mylar's largest side since 2026-10-14 (Colin: "If any dimension
     goes over 36" it should be a large custom stencil"), and so the size Large
     needs at least one side OVER (2026-10-15: "One dimension has to be over 36"
     in order for it to work on Large"). See sizeError. */
  var MYLAR_TOP = (DATA && DATA.mylarMax) || 36;
  var SLIDER_MIN = Math.max(MIN_DIM, Math.floor(MYLAR_TOP) + 1);   // 37"

  var STEPS = ['Upload', 'Size', 'Sheets', 'Details'];
  var STEP_LABEL = { Upload: 'Upload artwork', Size: 'Size it', Sheets: 'Sheets', Details: 'Review' };
  /* Colin, 2026-10-12: the last step read "Review" on the Next button and
     "Details" on the progress bar. The bar now uses the button's word. Only the
     visible label changes — the step is still 'Details' everywhere in code. */
  var BAR_LABEL = { Details: 'Review' };

  var ATC_LABEL = 'Add to cart';

  /* Shopify's ceiling for a line-item property file upload. */
  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

  /* Ceiling for the embedded artwork inside the designer SVG. Held well below
     MAX_UPLOAD_BYTES because the SVG is the THIRD attachment — the original and
     the cropped preview have to fit in the same request. */
  var SVG_ARTWORK_BUDGET = 12 * 1024 * 1024;

  /* ==================================================================
     MATHS
  ================================================================== */
  function snap4(n){ return Math.round(n * 4) / 4; }   // stencils are cut to the quarter inch
  function clampDim(v){ return Math.min(MAX_DIM, Math.max(MIN_DIM, v)); }
  function fmtDim(v){ return String(Math.round(v * 100) / 100); }
  function fmtFtDec(nIn){ return String(Math.round((nIn / 12) * 100) / 100); }

  /* Spelled out — "8 ft 5.25 in". The prime/double-prime tick marks were too
     easy to misread at these sizes (Colin, 2026-09-03). */
  function fmtFtInWords(nIn){
    var ft = Math.floor(nIn / 12);
    var rem = Math.round((nIn - ft * 12) * 4) / 4;
    if (rem >= 12) { ft += 1; rem = 0; }
    return rem ? ft + ' ft ' + fmtDim(rem) + ' in' : ft + ' ft';
  }
  /* Short form for the SVG dimension callouts, where there is no room for words. */
  function fmtFtIn(nIn){
    var ft = Math.floor(nIn / 12);
    var rem = Math.round((nIn - ft * 12) * 4) / 4;
    if (rem >= 12) { ft += 1; rem = 0; }
    return rem ? ft + '′ ' + fmtDim(rem) + '″' : ft + '′';
  }

  function esc(s){
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* Currency, ported from pricer-v2.js. The pricing table is in the shop's own
     currency but a visitor may be browsing in another one — Shopify converts at
     the cart, so a hardcoded "$" showed one number here and a different one at
     checkout. */
  function money(n){
    var c = window.Shopify && window.Shopify.currency;
    var rate = c && parseFloat(c.rate);
    var code = c && c.active;
    if (!code || !rate || !isFinite(rate)) return '$' + n.toFixed(2);
    var converted = n * rate;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: code
      }).format(converted);
    } catch (e) {
      return converted.toFixed(2) + ' ' + code;
    }
  }

  /* ==================================================================
     PRICING — Colin's real merged table, not a formula.

         order total = base + sizeAdder(w,h) + qtyAdder(area, qty)

     Both lookups round UP to the next step in the table: a 50" side is priced
     as 54", an area of 4608 sq in as the 6084 row, a quantity of 23 as 25
     (Colin, 2026-09-08 — "round up"). Up is the safe direction; it can never
     underprice an order. The identical maths lives in the backend's
     computeLargeQuote, which is what actually charges — see
     stencil-pricer-backend/app/pricing-calculator.server.js. If these two ever
     disagree the customer is shown one number and charged another, so they are
     generated from one spreadsheet and must be changed together.

     Every one of these returns null rather than a guess when the data is
     missing or the size is off the end of the table, so callers show "—"
     instead of inventing a number, and refreshGate() blocks the sale. */

  /* Index of the first step at or above v — the round-up rule, in one place. */
  function stepUpIdx(axis, v){
    if (!axis) return -1;
    for (var i = 0; i < axis.length; i++) if (axis[i] >= v) return i;
    return -1;                                  // past the end of the table
  }

  function sizeAdder(w, h){
    var c = stepUpIdx(SIZE_AXIS, w), r = stepUpIdx(SIZE_AXIS, h);
    if (c < 0 || r < 0 || !SIZE_ADDERS || !SIZE_ADDERS[r]) return null;
    var v = SIZE_ADDERS[r][c];
    return typeof v === 'number' ? v : null;
  }

  /* 0 at qty 1 for every area, which is what makes a single stencil come out at
     exactly base + sizeAdder. */
  function qtyAdder(area, q){
    var r = stepUpIdx(AREA_ROWS, area), c = stepUpIdx(QTY_COLS, q);
    if (r < 0 || c < 0 || !QTY_ADDERS || !QTY_ADDERS[r]) return null;
    var v = QTY_ADDERS[r][c];
    return typeof v === 'number' ? v : null;
  }

  /* What the sheet says the whole order costs, before any rounding. */
  function rawTotal(w, h, q){
    if (!DATA || typeof DATA.base !== 'number') return null;
    var s = sizeAdder(w, h);
    if (s == null) return null;
    var a = qtyAdder(w * h, q || 1);
    if (a == null) return null;
    return DATA.base + s + a;
  }

  /* The EFFECTIVE per-stencil price, rounded to the cent. There is no per-unit
     number in the sheet — quantity is priced as a whole order — so this is
     derived. It is rounded HERE, before multiplying back out, because this is
     the number the Shopify variant actually carries: the cart charges
     unitPrice × qty, so the total shown has to be built the same way round or
     shown and charged disagree by a cent or two on larger quantities. Same
     convention as the backend's computeLargeQuote. */
  function priceEach(w, h, q){
    q = q || 1;
    var t = rawTotal(w, h, q);
    if (t == null) return null;
    var per = Math.round((t / q) * 100) / 100;

    /* NEVER CHARGE MORE PER STENCIL FOR BUYING MORE OF THEM.
       The quantity sheet's area rows jump straight from 12,960 to 32,400 sq in
       with nothing between, and area rounds UP — so a design landing in that
       band (a 12 x 8 ft, say, at 13,824 sq in) has its quantity priced as if it
       were two and a half times the size. Measured against the shipped table
       that made bulk dearer per unit than singles on 4.5% of orderable
       size/quantity combinations: 12 x 8 ft came out $853.60 each at qty 5
       against $840.99 for one.

       No customer should ever be better off placing five separate orders, so
       the per-unit price is capped at the single-unit price. Worst case a bulk
       order costs exactly what buying them one at a time costs, which is always
       defensible and can never underprice. Raised with Colin 2026-09-08; if he
       fills the gap in the sheet this clamp simply stops being reachable, so it
       is safe to leave in either way. */
    var single = rawTotal(w, h, 1);
    if (single != null) {
      var one = Math.round(single * 100) / 100;
      if (per > one) per = one;
    }
    return per;
  }

  function priceTotal(w, h, q){
    var each = priceEach(w, h, q);
    return each == null ? null : Math.round(each * (q || 1) * 100) / 100;
  }

  /* The next quantity in the table that actually costs less per stencil, and by
     how much. Derived rather than declared: the old flat "5% / 10% / 15%" tiers
     were placeholders invented because there was no real matrix to read. There
     is one now. */
  function nextBreak(w, h, q){
    if (!QTY_COLS) return null;
    var here = priceEach(w, h, q);
    if (here == null) return null;
    for (var i = 0; i < QTY_COLS.length; i++) {
      if (QTY_COLS[i] <= q || QTY_COLS[i] > MAX_QTY) continue;
      var there = priceEach(w, h, QTY_COLS[i]);
      if (there == null || !(there < here - 0.005)) continue;
      /* Skip a break that rounds to "save 0%". It is real money, but too little
         to show: where the clamp is holding the per-unit price flat the next
         step down is a fraction of a percent (12 x 8 ft at qty 5 is $840.99
         against $840.62 at qty 8), and "Add 3 and save 0%" reads as broken.
         Keep walking the table instead of giving up — on the shipped sheet a
         genuine break always exists further along, so this only ever moves the
         nudge outward, never removes it. */
      var pct = Math.round((1 - there / here) * 100);
      if (pct < 1) continue;
      return { qty: QTY_COLS[i], pct: pct };
    }
    return null;
  }

  /* What the customer is already saving per stencil against ordering one. */
  function savedPct(w, h, q){
    if (q <= 1) return 0;
    var one = priceEach(w, h, 1), now = priceEach(w, h, q);
    if (one == null || now == null || !(now < one)) return 0;
    return Math.round((1 - now / one) * 100);
  }

  /* How many sheets a W×H design needs, trying the sheet both ways round and
     keeping whichever orientation needs fewer pieces. Ties break toward the
     squarer grid, which gives shorter seams. */
  function tile(W, H, sw0, sh0){
    var best = null;
    [[sw0, sh0], [sh0, sw0]].forEach(function (o) {
      var cols = Math.max(1, Math.ceil(W / o[0]));
      var rows = Math.max(1, Math.ceil(H / o[1]));
      var c = { cols: cols, rows: rows, pieces: cols * rows };
      if (!best || c.pieces < best.pieces ||
          (c.pieces === best.pieces && c.cols + c.rows < best.cols + best.rows)) best = c;
    });
    return best;
  }

  /* .ai files usually contain a PDF stream (Illustrator's "Create PDF Compatible
     File", on by default), so PDF.js can render them. AI yes, EPS no — EPS is
     PostScript and no browser can rasterise it (confirmed with Colin
     2026-08-19). MIME is unreliable for both, hence the extension check. */
  function isPdfLike(f){
    var name = (f.name || '').toLowerCase();
    return f.type === 'application/pdf' || /\.(pdf|ai)$/.test(name);
  }

  var pdfJsPromise = null;
  function loadPdfJs(src, workerSrc){
    if (pdfJsPromise) return pdfJsPromise;
    pdfJsPromise = new Promise(function (resolve, reject) {
      if (!src) { reject(new Error('pdf.js asset url missing')); return; }
      if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () {
        if (!window.pdfjsLib) { reject(new Error('pdf.js failed to define')); return; }
        if (workerSrc) window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        resolve(window.pdfjsLib);
      };
      s.onerror = function () { reject(new Error('pdf.js failed to load')); };
      document.head.appendChild(s);
    });
    return pdfJsPromise;
  }

  var FILE_ICON = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56"><rect width="56" height="56" fill="%23FFDE1B"/><text x="28" y="34" font-size="12" text-anchor="middle" font-family="sans-serif" font-weight="bold">FILE</text></svg>';

  /* ---------- icons — brand yellow #FFDE1B, see CHANGELOG 2026-09-04 ---------- */
  function iconUpload(s){
    s = s || 38;
    return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 46 46" fill="none" aria-hidden="true">' +
      '<rect x="4" y="6" width="38" height="34" rx="7" stroke="#141414" stroke-width="3" fill="#FFDE1B"/>' +
      '<path d="M23 30V15M23 15l-6.5 6.5M23 15l6.5 6.5" stroke="#141414" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  function iconLock(){
    return '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">' +
      '<rect x="4" y="9.5" width="14" height="9.5" rx="2.5" fill="#141414"/>' +
      '<path d="M7 9.5V7a4 4 0 018 0v2.5" stroke="#141414" stroke-width="2.6" fill="none"/></svg>';
  }
  function iconUnlock(){
    return '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">' +
      '<rect x="4" y="9.5" width="14" height="9.5" rx="2.5" fill="#141414"/>' +
      '<path d="M15 9V6.5a4 4 0 00-7.6-1.7" stroke="#141414" stroke-width="2.6" fill="none"/></svg>';
  }
  function iconRotate(){
    return '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
      '<rect x="3.5" y="7" width="10" height="10" rx="1.8" stroke="#141414" stroke-width="2.2"/>' +
      '<path d="M10.5 3.5h3a3 3 0 013 3v1" stroke="#141414" stroke-width="2.2" stroke-linecap="round" fill="none"/>' +
      '<path d="M12.6 1.6l-2.4 1.9 2.4 1.9" stroke="#141414" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
  }

  /* Two arrows pushing out to opposite corners of a frame, alongside the words.
     The icon carries no meaning on its own here — the label does — so it is
     aria-hidden and the button reads as its text. */
  function iconExpand(){
    return '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
      '<rect x="2.2" y="2.2" width="15.6" height="15.6" rx="1.8" stroke="#141414" stroke-width="2.2"/>' +
      '<path d="M7.4 7.4l-3 -3M4.4 7.4v-3h3" stroke="#141414" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M12.6 12.6l3 3M15.6 12.6v3h-3" stroke="#141414" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  class PricerLargeApp extends HTMLElement {
    connectedCallback(){
      this.state = {
        stepIdx: 0,
        maxStep: 0,
        /* 8 × 4 ft — the commonest real large order, and it is wider than it is
           tall, which makes the rotate affordance discoverable on step 2. */
        w: 96, h: 48,
        /* Unlocked by default, same as mylar (Colin, 2026-08-21): the first
           thing a customer does is drag a frame round their design, and starting
           locked makes that snap back instead of working. */
        locked: false,
        lockRatio: null,   // the w/h the lock holds; see lockedRatio()
        oversize: false,
        /* Feet, not inches (Colin, 2026-09-06): "have it default to feet not
           inches on this one". This is the LARGE product — the slider runs 1 to
           30 ft, every preset chip is labelled in feet, and nobody orders a
           mural in inches. state.w/h stay in inches regardless; the unit only
           decides how the two fields are written. */
        unit: 'ft',
        sheetId: (SHEETS[0] && SHEETS[0].id) || 'standard',
        qty: 1,
        artUploaded: false,
        sendLater: false,
        artW: null, artH: null,
        fullSrc: null,
        previewable: false,
        savedCrop: null,
        fitMode: null,
        uploadError: null,
        pausedUpload: null
      };
      this.crop = null;        // {x,y,w,h} in displayed pixels, only while the Size card is mounted
      this.dragMode = null;
      this.dragStart = null;

      if (this.dataset.contactUrl) CONTACT_URL = this.dataset.contactUrl;

      this.bindEls();
      this.form = this.querySelector('form');
      this.bindPersistentEvents();

      /* The review modal is position:fixed and must live directly on <body>. Left
         inside the section, a theme animation utility applying a transform to an
         ancestor silently breaks fixed positioning and renders it off-screen
         while its backdrop still shows — that was the v1 crop-modal bug. */
      if (this.el.cropModal && this.el.cropModal.parentNode !== document.body) {
        document.body.appendChild(this.el.cropModal);
      }

      this.render('fwd');
    }

    disconnectedCallback(){
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onPointerMove) {
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
      }
      if (this._onWinResize) {
        window.removeEventListener('resize', this._onWinResize);
        window.removeEventListener('orientationchange', this._onWinResize);
      }
      if (this.el && this.el.cropModal && this.el.cropModal.parentNode === document.body) {
        document.body.removeChild(this.el.cropModal);
      }
    }

    $(ref){ return this.querySelector('[data-ref="' + ref + '"]'); }

    bindEls(){
      var refs = ['progress','cardhost','stash','qty','fileInput','croppedInput','designerInput',
        'tileInput','atcBtn','atcNote','noPreviewNote','noPreviewAck','noPreviewSize',
        'propWidth','propHeight','propArtStatus','propSheets','propPieceSize'];
      this.el = {};
      var self = this;
      refs.forEach(function (r) { self.el[r] = self.$(r); });
      // The modal is moved to <body>, so it stops being a descendant — hold a
      // direct reference rather than re-querying through $().
      this.el.cropModal = this.querySelector('[data-ref="cropModal"]');
      if (this.el.cropModal) {
        this.el.cropModalImg = this.el.cropModal.querySelector('[data-ref="cropModalImg"]');
        this.el.cropModalBox = this.el.cropModal.querySelector('[data-ref="cropModalBox"]');
      }
    }

    /* Listeners on nodes that live in the stash and get moved between cards.
       Bound once — a card re-render MOVES these nodes, it never recreates them,
       so their listeners and their FileLists survive. */
    bindPersistentEvents(){
      var self = this;

      this.el.fileInput.addEventListener('change', function (e) {
        self.onFileChosen(e.target.files[0]);
        if (!e.target.files.length) e.target.value = '';
      });

      if (this.el.noPreviewAck) {
        this.el.noPreviewAck.addEventListener('change', function () {
          self.state.fitMode = this.checked ? 'unverified' : null;
          self.refreshGate();
        });
      }

      /* Add to Cart is type="button", not type="submit" — deliberately, so the
         whole sequence is ours: fetch the real price, swap in the correctly
         priced variant, THEN trigger the real form submission, which the
         theme's own <product-form> handles exactly as it does everywhere else. */
      this.el.atcBtn.addEventListener('click', function () { self.handleAddToCart(); });

      if (this.el.cropModal) {
        this.el.cropModal.addEventListener('click', function () {
          self.el.cropModal.classList.remove('show');
          document.body.classList.remove('pricer-lock');
        });
      }

      /* Pointer drags are tracked on window, not the crop stage: a fast drag
         routinely leaves the element before the pointer is released, and losing
         the move events there strands the box mid-drag. */
      this._onPointerMove = function (e) { self.onCropPointerMove(e); };
      this._onPointerUp = function () { self.dragMode = null; };
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);

      /* Re-measure the crop stage when the viewport changes — see reflowCrop.
         rAF-coalesced so a drag-resize doesn't run the maths per pixel. */
      var reflowQueued = false;
      this._onWinResize = function () {
        if (reflowQueued) return;
        reflowQueued = true;
        requestAnimationFrame(function () {
          reflowQueued = false;
          self.reflowCrop();
        });
      };
      window.addEventListener('resize', this._onWinResize);
      window.addEventListener('orientationchange', this._onWinResize);

      this._onKeydown = function (e) {
        if (e.key === 'Escape' && self.el.cropModal && self.el.cropModal.classList.contains('show')) {
          self.el.cropModal.classList.remove('show');
          document.body.classList.remove('pricer-lock');
        }
      };
      document.addEventListener('keydown', this._onKeydown);

      /* No swipe-between-steps gesture — removed 2026-10-04 at Colin's request,
         same as Custom Mylar. The Back link and the progress circles are the
         only ways between steps. */
    }

    /* ==================================================================
       NAVIGATION
    ================================================================== */
    stepName(){ return STEPS[this.state.stepIdx]; }
    goNext(){
      var i = Math.min(this.state.stepIdx + 1, STEPS.length - 1);
      if (this.blockedBySize(i)) return;
      this.state.stepIdx = i; this.render('fwd');
    }
    goBack(){ if (this.state.stepIdx > 0) { this.state.stepIdx -= 1; this.render('back'); } }
    jumpTo(i){
      if (this.blockedBySize(i)) return;
      var dir = i < this.state.stepIdx ? 'back' : 'fwd';
      this.state.stepIdx = Math.max(0, Math.min(i, STEPS.length - 1));
      this.render(dir);
    }

    /* Unit-aware helpers — the Size step's inputs read and write in whichever
       unit is selected, while state.w/state.h are ALWAYS inches. Every price,
       property and tiling calculation therefore stays in one unit and the
       toggle is purely presentational. */
    toIn(v){ return this.state.unit === 'ft' ? v * 12 : v; }
    fmtUnit(nIn){ return this.state.unit === 'ft' ? fmtFtDec(nIn) : fmtDim(nIn); }
    nudgeStep(){ return this.state.unit === 'ft' ? 6 : 1; }   // half a foot, or an inch
    fmtSizeChosen(W, H){
      return this.state.unit === 'ft'
        ? fmtFtInWords(W) + ' × ' + fmtFtInWords(H)
        : fmtDim(W) + '" × ' + fmtDim(H) + '"';
    }

    /* UNDER THE MINIMUM IS FLAGGED, NEVER QUIETLY RAISED (Colin, 2026-10-08,
       first on Mylar: "If someone types a 1 it can't just turn into a 2
       without any sort of flag ... they can't proceed to next step"; then for
       Large and QR: "Yes. It is bad form to do that"). A typed size below
       MIN_DIM is kept as typed; the Size step shows a note in the customer's
       unit and nothing past it can be reached until both sides are at least
       MIN_DIM. Same rule as pricer-v2.js. Returns the note's text, or ''.

       AND ONE SIDE MUST BE OVER 36" (Colin, 2026-10-15: "One dimension has to
       be over 36" in order for it to work on Large ... make the min go down to
       2" but the other dimension needs to be over 36""). Up to 36" on both
       sides is a Custom Mylar stencil; paintSize links there. Checked first,
       because a size that fails it belongs on the other page whatever else is
       wrong with it. */
    sizeError(){
      var self = this;
      var u = function (nIn) {
        return self.state.unit === 'ft' ? fmtFtDec(Math.max(0, nIn)) + ' ft' : fmtDim(Math.max(0, nIn)) + '"';
      };
      /* The 2" floor and anything under it read in inches even on the feet
         toggle; "0.17 ft" means nothing to anyone. */
      var small = function (nIn) {
        return self.state.unit === 'ft' ? fmtDim(Math.max(0, nIn)) + ' in' : fmtDim(Math.max(0, nIn)) + '"';
      };
      if (this.mylarSized()) {
        var top = u(MYLAR_TOP);
        return 'Large Custom Stencils need at least one side over ' + top +
          '. Make your width or height bigger than ' + top + ' to continue.';
      }
      var lowW = !(this.state.w >= MIN_DIM), lowH = !(this.state.h >= MIN_DIM);
      if (!lowW && !lowH) return '';
      var min = small(MIN_DIM);
      return 'Each side must be at least ' + min + '. ' + (lowW && lowH
        ? 'Make your width and height at least ' + min + ' to continue.'
        : 'Your ' + (lowW ? 'width' : 'height') + ' is ' + small(lowW ? this.state.w : this.state.h) +
          ' — make it at least ' + min + ' to continue.');
    }
    /* Both sides at or under Mylar's 36": a Custom Mylar size, not a Large one.
       (A side not yet typed counts as neither, so it falls to the 2" check.) */
    mylarSized(){
      var w = this.state.w, h = this.state.h;
      return Number.isFinite(w) && Number.isFinite(h) && Math.max(w, h) <= MYLAR_TOP + 0.001;
    }
    /* Every forward route (Next, a progress circle, the post-upload advance)
       passes through here. On Size it shows the note and stays put; from
       anywhere else it brings the customer back to Size to fix it. */
    blockedBySize(i){
      var sizeIdx = STEPS.indexOf('Size');
      if (i <= sizeIdx || !this.sizeError()) return false;
      if (this.stepName() === 'Size') this.paintSize();
      else this.jumpTo(sizeIdx);
      return true;
    }

    /* The shape the lock is holding. Captured when the lock goes on and when a
       chip or rotate sets an explicit new shape; falls back to the live sides
       for a state that predates it. */
    lockedRatio(){
      return this.state.lockRatio || (clampDim(this.state.w) / clampDim(this.state.h));
    }
    relockRatio(){
      if (this.state.locked) this.state.lockRatio = clampDim(this.state.w) / clampDim(this.state.h);
    }

    /* noFloor (typing only) keeps the ceiling but not the floor, so an
       under-minimum entry is flagged by sizeError instead of silently raised. */
    setPair(w, r, noFloor){
      if (!Number.isFinite(w) || !Number.isFinite(r) || r <= 0) return;
      w = Math.min(w, MAX_DIM, MAX_DIM * r);
      if (!noFloor) w = Math.max(w, MIN_DIM, MIN_DIM * r);
      this.state.w = snap4(w);
      this.state.h = snap4(w / r);
    }

    /* ==================================================================
       RENDER
    ================================================================== */
    render(dir){
      /* Leaving the Size card bakes the crop first, by whatever route the
         customer left it — Next, Back, or a progress circle. */
      this.bakeCrop();
      this.returnPersistentNodes();

      this.state.maxStep = Math.max(this.state.maxStep, this.state.stepIdx);
      this.renderProgress();

      var host = this.el.cardhost;
      host.textContent = '';
      var card = document.createElement('div');
      card.className = 'pricer-card' + (dir === 'back' ? ' is-back' : '');

      var name = this.stepName();
      if (name === 'Upload') this.renderUpload(card);
      else if (name === 'Size') this.renderSize(card);
      else if (name === 'Sheets') this.renderSheets(card);
      else this.renderDetails(card);

      host.appendChild(card);

      /* Crop setup has to happen AFTER the card is in the document: it measures
         the image with clientWidth/clientHeight, which are 0 for a detached
         node, so initialising inside renderSize() silently produced no crop at
         all — no cropped preview, no designer SVG. Synchronously first (reading
         clientWidth on an attached node forces layout, so the crop is right on
         the very first paint), then once more on rAF as a backup. initCrop is
         idempotent, so running twice costs nothing. */
      if (this.stepName() === 'Size' && this._size && this._size.hasArt) {
        var self = this;
        this.initCrop();
        requestAnimationFrame(function () { self.initCrop(); });
      }

      this.updatePrice();
    }

    /* Puts the moved-around nodes back in the stash before the card host is
       wiped, so wiping can never destroy the file inputs or the Add to Cart
       button along with the card that was borrowing them. */
    returnPersistentNodes(){
      var self = this;
      ['atcBtn', 'atcNote', 'noPreviewNote'].forEach(function (ref) {
        var node = self.el[ref];
        if (node && node.parentNode !== self.el.stash) self.el.stash.appendChild(node);
      });
    }

    renderProgress(){
      var self = this;
      var wrap = this.el.progress;
      wrap.textContent = '';
      STEPS.forEach(function (name, i) {
        var reached = i <= self.state.maxStep;
        var isNow = i === self.state.stepIdx;
        var label = BAR_LABEL[name] || name;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pricer-pstep' + (isNow ? ' is-now' : reached ? ' is-done' : '');
        b.innerHTML = '<span class="dot">' + (reached && !isNow ? '✓' : (i + 1)) + '</span>' +
          '<span class="plbl">' + esc(label) + '</span>';
        if (reached && !isNow) {
          b.setAttribute('aria-label', 'Go to ' + label);
          b.addEventListener('click', function () { self.jumpTo(i); });
        } else {
          b.tabIndex = -1;
          if (isNow) b.setAttribute('aria-current', 'step');
        }
        wrap.appendChild(b);
      });
    }

    /* Card footer: Back on the left, the forward action on the right. The
       Details step passes its own node (the real Add to Cart button) instead of
       getting a generated one. */
    addNav(card, opts){
      opts = opts || {};
      var self = this;
      var nav = document.createElement('div');
      nav.className = 'pricer-cardnav';

      if (opts.backOk !== false) {
        var back = document.createElement('button');
        back.type = 'button';
        back.className = 'pricer-backlink';
        back.textContent = '‹ Back';
        back.addEventListener('click', function () { self.goBack(); });
        nav.appendChild(back);
      } else {
        nav.appendChild(document.createElement('span'));
      }

      if (opts.node) {
        nav.appendChild(opts.node);
        this._navNext = null;
      } else {
        var nextName = STEPS[this.state.stepIdx + 1];
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pricer-btn pricer-btn--primary';
        btn.textContent = nextName ? 'Next: ' + STEP_LABEL[nextName] + ' →' : 'Continue →';
        btn.disabled = !!opts.nextDisabled;
        btn.addEventListener('click', function () { self.goNext(); });
        nav.appendChild(btn);
        this._navNext = btn;
      }
      card.appendChild(nav);

      /* Running total, bottom-right, from the Size step onward — the customer
         should never be choosing a size or a quantity without seeing the cost. */
      if (this.state.stepIdx >= STEPS.indexOf('Size')) {
        var price = document.createElement('div');
        price.className = 'pricer-cardprice';
        /* The total is the whole ORDER, so at qty > 1 it needs saying out loud —
           without the caption the Size step shows $4,204.95 for a 12 x 8 ft and
           reads as a per-stencil price four times too high. */
        price.innerHTML = '<span class="total" data-role="cardtotal">—</span>' +
                          '<span class="forqty" data-role="cardqty"></span>';
        card.appendChild(price);
      }
      return nav;
    }

    /* ==================================================================
       STEP 1 · UPLOAD
    ================================================================== */
    renderUpload(card){
      var self = this;
      card.innerHTML =
        '<h2 class="pricer-h2">Upload your artwork</h2>' +
        '<p class="pricer-sub">Choose the image you want turned into a large stencil.</p>' +
        '<div class="pricer-drop" data-role="drop">' +
          iconUpload(38) +
          '<div class="pricer-drop__big">Drag &amp; drop or choose a file</div>' +
          '<p class="pricer-drop__formats">AI, EPS, SVG, PDF, PSD, PNG, JPG, TIFF</p>' +
          '<button type="button" class="pricer-btn pricer-btn--primary pricer-drop__btn" data-role="choose">Choose file</button>' +
        '</div>' +
        '<p class="pricer-uploaderr" data-role="err"></p>' +
        '<div class="pricer-filelist" data-role="filerow"></div>' +
        '<button type="button" class="pricer-emaillater" data-role="later"></button>' +
        '<div class="pricer-howworks" data-role="how">' +
          '<strong>Here’s how it works:</strong> after checkout, reply to your order confirmation email with your file, and we’ll get started on your free proof as soon as it arrives.' +
        '</div>';

      var drop = card.querySelector('[data-role="drop"]');
      card.querySelector('[data-role="choose"]').addEventListener('click', function () { self.el.fileInput.click(); });
      drop.addEventListener('dragover', function (e) { e.preventDefault(); drop.classList.add('is-over'); });
      drop.addEventListener('dragleave', function () { drop.classList.remove('is-over'); });
      drop.addEventListener('drop', function (e) {
        e.preventDefault();
        drop.classList.remove('is-over');
        var f = e.dataTransfer.files[0];
        if (f) self.onFileChosen(f);
      });

      card.querySelector('[data-role="later"]').addEventListener('click', function () { self.toggleSendLater(); });

      // The no-preview acknowledgement belongs with the file that triggered it.
      card.appendChild(this.el.noPreviewNote);

      this.addNav(card, { backOk: false, nextDisabled: !this.canLeaveUpload() });
      this.paintUpload();
    }

    canLeaveUpload(){
      if (this.state.sendLater) return true;
      if (!this.state.artUploaded) return false;
      /* A file we can't rasterise skips the proportion check every other file
         has to pass, so it needs the explicit acknowledgement first. Absent
         from Colin's prototype, which let an EPS or PSD through unchecked. */
      if (!this.state.previewable && this.el.noPreviewAck && !this.el.noPreviewAck.checked) return false;
      return true;
    }

    paintUpload(){
      var self = this;
      var card = this.el.cardhost.firstChild;
      if (!card || this.stepName() !== 'Upload') return;

      var later = card.querySelector('[data-role="later"]');
      later.classList.toggle('is-on', this.state.sendLater);
      later.innerHTML = this.state.sendLater
        ? '✓ You’ll email artwork after ordering — <u>undo</u>'
        : 'Still working on your file? Email it after ordering';
      card.querySelector('[data-role="how"]').classList.toggle('show', this.state.sendLater);
      card.querySelector('[data-role="err"]').textContent = this.state.uploadError || '';

      var row = card.querySelector('[data-role="filerow"]');
      row.textContent = '';
      if (this.state.artUploaded) {
        var item = document.createElement('div');
        item.className = 'pricer-fileitem';
        var img = document.createElement('img');
        img.alt = '';
        img.src = this.state.fullSrc || FILE_ICON;
        var nameEl = document.createElement('span');
        nameEl.className = 'fname';
        nameEl.textContent = this._fileName || 'Your artwork';
        /* Only speaks while there is something to say. A finished, previewable
           upload says nothing — the thumbnail beside the filename IS the
           confirmation. The slow paths and the hand-check note still speak,
           because those the customer can't see for themselves. */
        var status = document.createElement('span');
        status.className = 'fstatus';
        status.textContent = this._fileStatus || '';
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'fremove';
        remove.setAttribute('aria-label', 'Remove file');
        remove.textContent = '✕';
        remove.addEventListener('click', function () { self.clearArtwork(); self.paintUpload(); self.refreshGate(); });
        var meta = document.createElement('span');
        meta.className = 'fmeta';
        meta.appendChild(nameEl);
        if (status.textContent) meta.appendChild(status);
        item.appendChild(img);
        item.appendChild(meta);
        item.appendChild(remove);
        row.appendChild(item);
      }

      var needsAck = this.state.artUploaded && !this.state.previewable;
      this.el.noPreviewNote.classList.toggle('show', needsAck);
      if (needsAck && this.el.noPreviewSize) {
        this.el.noPreviewSize.textContent = this.fmtSizeChosen(clampDim(this.state.w), clampDim(this.state.h));
      }

      if (this._navNext) this._navNext.disabled = !this.canLeaveUpload();
    }

    clearArtwork(){
      this.state.artUploaded = false;
      this.state.artW = null; this.state.artH = null;
      this.state.fullSrc = null; this.state.previewable = false;
      this.state.savedCrop = null;
      this.state.fitMode = null; this.state.uploadError = null;
      this._fileName = null; this._fileStatus = null;
      this._croppedThumb = null;
      this.crop = null;
      this.el.fileInput.value = '';
      if (this.el.croppedInput) this.el.croppedInput.value = '';
      if (this.el.designerInput) this.el.designerInput.value = '';
      if (this.el.noPreviewAck) this.el.noPreviewAck.checked = false;
    }

    /* ==================================================================
       STEP 2 · SIZE (dimensions + crop together)
    ================================================================== */
    renderSize(card){
      var self = this;
      var hasArt = this.state.artUploaded && this.state.previewable && !!this.state.fullSrc;
      var uid = this.dataset.uid || 'lg';

      var presetHtml = PRESETS.map(function (p) {
        return '<button type="button" class="pricer-preset" data-w="' + p.w + '" data-h="' + p.h + '">' + esc(p.l) + '</button>';
      }).join('');

      /* NO min/max ATTRIBUTES on these inputs, deliberately. requestSubmit()
         runs the browser's own constraint validation across the whole form, and
         these fields carry FEET when the unit toggle is on feet — a min of 12
         would then reject a perfectly legal "4 ft" and block the submit with no
         event and no error (the same class of failure as the missing step="any"
         caught live on 2026-08-12). Range is enforced by clampDim instead. */
      card.innerHTML =
        '<h2 class="pricer-h2">Size it</h2>' +
        '<p class="pricer-sub">Drag the crop or type a size.</p>' +
        '<div class="pricer-unitrow">' +
          '<label>Size</label>' +
          '<div class="pricer-units" role="group" aria-label="Units">' +
            '<button type="button" class="pricer-unit" data-unit="in">inches</button>' +
            '<button type="button" class="pricer-unit" data-unit="ft">feet</button>' +
          '</div>' +
        '</div>' +
        '<div class="pricer-sizerow">' +
          '<div class="pricer-field">' +
            '<label for="pricer-lg-w-' + uid + '" data-role="lblW">Width (' + esc(this.state.unit) + ')</label>' +
            '<div class="pricer-nudge">' +
              '<button type="button" class="minibtn" data-role="wMinus" aria-label="Decrease width">−</button>' +
              '<input id="pricer-lg-w-' + uid + '" data-role="inW" class="nospin" type="number" step="any" inputmode="decimal">' +
              '<button type="button" class="minibtn" data-role="wPlus" aria-label="Increase width">+</button>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="pricer-lockbtn" data-role="lock" aria-pressed="' + this.state.locked + '"></button>' +
          '<div class="pricer-field">' +
            '<label for="pricer-lg-h-' + uid + '" data-role="lblH">Height (' + esc(this.state.unit) + ')</label>' +
            '<div class="pricer-nudge">' +
              '<button type="button" class="minibtn" data-role="hMinus" aria-label="Decrease height">−</button>' +
              '<input id="pricer-lg-h-' + uid + '" data-role="inH" class="nospin" type="number" step="any" inputmode="decimal">' +
              '<button type="button" class="minibtn" data-role="hPlus" aria-label="Increase height">+</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<p class="pricer-ftline" data-role="ftline"></p>' +
        '<div class="pricer-sliderwrap">' +
          /* The slider is the LONGER side, so it starts just over Mylar's 36":
             anything lower is a Mylar size (see sizeError). The 2" floor is
             for the short side, which the fields and +/− still reach. */
          '<input type="range" data-role="slider" min="' + SLIDER_MIN + '" max="' + MAX_DIM + '" step="1" aria-label="Scale size">' +
          '<div class="ends"><span>' + fmtFtInWords(SLIDER_MIN) + '</span><span>Drag to scale</span><span>' + fmtFtInWords(MAX_DIM) + '</span></div>' +
        '</div>' +
        '<p class="pricer-oversize" data-role="oversize"></p>' +
        '<p class="pricer-oversize pricer-minsize" data-role="minsize" role="alert"></p>' +
        '<div class="pricer-presets" data-role="presets">' + presetHtml + '</div>' +
        (hasArt
          ? '<div class="pricer-cropouter">' +
              '<div class="pricer-cropwrap" data-role="cropwrap">' +
                '<img data-role="cropimg" src="' + esc(this.state.fullSrc) + '" alt="Your uploaded design">' +
                '<div class="pricer-cropbox" data-role="cropbox">' +
                  '<div class="handle h-nw" data-h="nw"></div><div class="handle h-ne" data-h="ne"></div>' +
                  '<div class="handle h-sw" data-h="sw"></div><div class="handle h-se" data-h="se"></div>' +
                '</div>' +
              '</div>' +
              '<button type="button" class="pricer-croprotate" data-role="rotate" title="Rotate — swap width and height" aria-label="Rotate: swap width and height">' + iconRotate() + '</button>' +
              '<button type="button" class="pricer-cropwhole" data-role="whole">' + iconExpand() + '<span>Use the whole image</span></button>' +
            '</div>'
          : (this.state.sendLater
              ? '<p class="pricer-upnote">Your size for reference — we’ll fit your emailed artwork to it.</p>'
              : '<p class="pricer-upnote">Your size for reference — a designer will check your file against it.</p>'));

      var inW = card.querySelector('[data-role="inW"]');
      var inH = card.querySelector('[data-role="inH"]');
      var slider = card.querySelector('[data-role="slider"]');
      var lockBtn = card.querySelector('[data-role="lock"]');

      this._size = {
        card: card, inW: inW, inH: inH, slider: slider, lockBtn: lockBtn,
        img: card.querySelector('[data-role="cropimg"]'),
        box: card.querySelector('[data-role="cropbox"]'),
        hasArt: hasArt
      };
      this._typeAnchor = null;   // a fresh card has no entry in progress

      /* Locked, each side drags the other along at the shape that was locked.
         The ratio is the one captured when the lock went on (lockedRatio), not
         re-read from the rounded sides on every keystroke or tap, so
         quarter-inch rounding can't creep the shape over a long entry. */
      var applyW = function (vIn) {
        if (!Number.isFinite(vIn)) return;
        if (self.state.locked) self.setPair(clampDim(vIn), self.lockedRatio());
        else self.state.w = snap4(clampDim(vIn));
        self.paintSize();
      };
      var applyH = function (vIn) {
        if (!Number.isFinite(vIn)) return;
        if (self.state.locked) {
          var r = self.lockedRatio();
          self.setPair(clampDim(vIn) * r, r);
        } else self.state.h = snap4(clampDim(vIn));
        self.paintSize();
      };

      /* The crop as it stood when this entry began. Mid-typing the crop is
         refitted from THIS rather than from itself — the intermediate values
         ("1" on the way to "15") are wild shapes, and refitting from the last
         keystroke's frame would ratchet it down to a sliver. Cleared once the
         customer leaves both fields. */
      var anchorCrop = function () {
        if (!self._typeAnchor && self.crop) {
          self._typeAnchor = { x: self.crop.x, y: self.crop.y, w: self.crop.w, h: self.crop.h };
        }
      };

      /* input tracks keystrokes live for the price WITHOUT clamping-and-
         rewriting the field on every character — rewriting mid-typing was
         actively hostile to typing itself ("14" begins as "1"). Clamping happens
         once, on focusout. Flagged by Colin, 2026-08-11. */
      inW.addEventListener('input', function () {
        var v = self.toIn(parseFloat(inW.value));
        if (!Number.isFinite(v)) return;
        anchorCrop();
        self.state.oversize = v > MAX_DIM;
        // noFloor: under the minimum is kept as typed and flagged (see sizeError).
        if (self.state.locked) self.setPair(Math.min(v, MAX_DIM), self.lockedRatio(), true);
        else self.state.w = Math.min(v, MAX_DIM);
        self.paintSize({ typing: true });
      });
      inH.addEventListener('input', function () {
        var v = self.toIn(parseFloat(inH.value));
        if (!Number.isFinite(v)) return;
        anchorCrop();
        self.state.oversize = v > MAX_DIM;
        if (self.state.locked) {
          var r = self.lockedRatio();
          self.setPair(Math.min(v, MAX_DIM) * r, r, true);
        } else self.state.h = Math.min(v, MAX_DIM);
        self.paintSize({ typing: true });
      });
      /* focusout finalizes the pair once the customer is done with BOTH fields,
         not the instant either loses focus — width and height are one logical
         entry and tabbing between them is mid-entry, not "done". activeElement
         on a deferred tick rather than e.relatedTarget, which is only reliably
         populated for keyboard-driven focus moves. */
      [inW, inH].forEach(function (input) {
        input.addEventListener('focusout', function () {
          setTimeout(function () {
            var active = document.activeElement;
            if (active === inW || active === inH) return;
            self._typeAnchor = null;
            /* Ceiling only. Under the minimum is no longer floored here — it
               stays as typed and paintSize flags it (see sizeError). */
            self.state.w = snap4(Math.min(self.state.w, MAX_DIM));
            self.state.h = snap4(Math.min(self.state.h, MAX_DIM));
            self.state.oversize = false;
            self.paintSize();
          }, 0);
        });
      });

      card.querySelector('[data-role="wMinus"]').addEventListener('click', function () { applyW(self.state.w - self.nudgeStep()); });
      card.querySelector('[data-role="wPlus"]').addEventListener('click', function () { applyW(self.state.w + self.nudgeStep()); });
      card.querySelector('[data-role="hMinus"]').addEventListener('click', function () { applyH(self.state.h - self.nudgeStep()); });
      card.querySelector('[data-role="hPlus"]').addEventListener('click', function () { applyH(self.state.h + self.nudgeStep()); });

      card.querySelectorAll('.pricer-unit').forEach(function (b) {
        b.addEventListener('click', function () {
          self.state.unit = b.dataset.unit;
          /* fromCrop, so switching units doesn't reshape the crop box — nothing
             about the stencil changed, only how it's written down. */
          self.paintSize({ fromCrop: true });
        });
      });

      /* The ratio is anchored at gesture start so quarter-inch rounding on each
         tick can't slowly drift the shape over a long drag. */
      var sliderRatio = null;
      ['mousedown', 'touchstart', 'keydown'].forEach(function (ev) {
        slider.addEventListener(ev, function () { sliderRatio = self.state.w / self.state.h; }, { passive: true });
      });
      slider.addEventListener('change', function () { sliderRatio = null; });
      slider.addEventListener('input', function () {
        var target = parseFloat(slider.value);
        var r = sliderRatio || (self.state.w / self.state.h);
        if (r >= 1) self.setPair(target, r);
        else self.setPair(target * r, r);
        self.paintSize();
      });

      lockBtn.addEventListener('click', function () {
        /* TAPPING THE LOCK CHANGES NOTHING ON SCREEN (Colin, 2026-10-04/05:
           "When I clicked the lock button the proportion changed").

           It used to. Typing a size never reshaped the crop frame — that waited
           for the field to lose focus — so after framing the artwork and typing
           a width, the frame and the numbers were two different shapes. Tapping
           the lock then snapped the frame to the numbers, and the proportion the
           customer had framed was gone the instant they tried to keep it.

           The frame now follows the numbers live while typing (see paintSize),
           so the two are already the same shape when the lock is tapped. This
           only captures that shape; it does not fit, move or resize anything. */
        self.state.locked = !self.state.locked;
        self.state.lockRatio = self.state.locked
          ? clampDim(self.state.w) / clampDim(self.state.h)
          : null;
        self.paintSize({ fromCrop: true });
      });

      var presets = card.querySelector('[data-role="presets"]');
      var dragged = this.enableDragScroll(presets);
      presets.querySelectorAll('.pricer-preset').forEach(function (b) {
        b.addEventListener('click', function () {
          if (dragged()) return;   // a drag that ended over a chip is not a tap on it
          self.state.w = parseFloat(b.dataset.w);
          self.state.h = parseFloat(b.dataset.h);
          self.relockRatio();   // a chip is an explicit shape; the lock keeps that one now
          self.paintSize();
        });
      });

      if (hasArt) {
        this._size.box.addEventListener('pointerdown', function (e) { self.onCropPointerDown(e); });
        var rotate = card.querySelector('[data-role="rotate"]');
        var whole = card.querySelector('[data-role="whole"]');
        /* These buttons sit over the crop stage; without this a press on one
           also starts a crop drag. */
        [rotate, whole].forEach(function (btn) {
          ['pointerdown', 'mousedown', 'touchstart'].forEach(function (ev) {
            btn.addEventListener(ev, function (e) { e.stopPropagation(); });
          });
        });
        rotate.addEventListener('click', function (e) { e.stopPropagation(); self.rotateSize(); });
        whole.addEventListener('click', function (e) { e.stopPropagation(); self.useWholeImage(); });
        if (this._size.img.complete && this._size.img.naturalWidth) this.initCrop();
        else this._size.img.addEventListener('load', function () { self.initCrop(); });
      }

      this.addNav(card, { backOk: true });
      this.paintSize();
    }

    /* "Obviously I need to be able to go out to the whole image" (Colin,
       2026-09-06). Even with the ceiling no longer pinning the frame, walking
       four corners out to four edges on a phone is a fight — each drag can only
       reach as far as the opposite corner, which does not move, so filling the
       artwork takes an alternating sequence of grabs and nudges. This is the
       one tap that does it.

       Scale is carried across UNIFORMLY, the same rule an unlocked drag
       follows: framing more artwork orders a bigger stencil, at the same
       inches-per-pixel, capped at the 30 ft ceiling. Uniform because kx and ky
       can have been left apart by an earlier ceiling rebound, and the whole
       image has ONE shape — taking the larger of the two makes the ordered
       design the artwork's true proportions rather than inheriting a skew. */
    useWholeImage(){
      if (!this._size || !this._size.img || !this.crop) return;
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (!W || !H || !this.crop.w || !this.crop.h) return;

      var k = Math.max(this.state.w / this.crop.w, this.state.h / this.crop.h);

      this.crop = { x: 0, y: 0, w: W, h: H };
      this.clampCrop();
      this.paintCrop();

      /* Locked means "the frame keeps the numbers' shape". Framing everything is
         an explicit instruction about shape, and the artwork only matches the
         locked ratio by coincidence, so the lock comes off rather than the
         request being silently ignored. paintSize repaints the button. */
      this.state.locked = false;
      this.state.lockRatio = null;

      var rawW = W * k, rawH = H * k;
      var fit = Math.min(1, MAX_DIM / rawW, MAX_DIM / rawH);
      this.state.w = snap4(clampDim(rawW * fit));
      this.state.h = snap4(clampDim(rawH * fit));

      /* fromCrop: the frame is already exactly where it was asked to be, and
         fitCropToRatio would only round it back off the edges. */
      this.paintSize({ fromCrop: true });
    }

    rotateSize(){
      var t = this.state.w; this.state.w = this.state.h; this.state.h = t;
      this.relockRatio();
      if (this.crop && this._size && this._size.img) {
        var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
        if (W && H) {
          var c = this.crop;
          var cx = c.x + c.w / 2, cy = c.y + c.h / 2;
          var nw = c.h, nh = c.w;
          var s = Math.min(1, W / nw, H / nh);
          nw *= s; nh *= s;
          c.w = nw; c.h = nh;
          c.x = cx - nw / 2; c.y = cy - nh / 2;
          this.clampCrop();
          this.paintCrop();
        }
      }
      this.paintSize({ fromCrop: true });
    }

    paintSize(opts){
      opts = opts || {};
      if (!this._size || this.stepName() !== 'Size') return;
      var s = this._size;
      var W = clampDim(this.state.w), H = clampDim(this.state.h);

      if (!opts.typing) {
        s.inW.value = this.fmtUnit(this.state.w);
        s.inH.value = this.fmtUnit(this.state.h);
      } else if (this.state.locked) {
        /* Mid-typing, only the OTHER field is rewritten — rewriting the one
           under the cursor would fight the customer's keystrokes. */
        var mirror = (document.activeElement === s.inW) ? s.inH : s.inW;
        mirror.value = (mirror === s.inW) ? this.fmtUnit(this.state.w) : this.fmtUnit(this.state.h);
      }

      s.card.querySelectorAll('.pricer-unit').forEach(function (b) {
        b.classList.toggle('is-on', b.dataset.unit === this.state.unit);
      }, this);
      s.card.querySelector('[data-role="lblW"]').textContent = 'Width (' + this.state.unit + ')';
      s.card.querySelector('[data-role="lblH"]').textContent = 'Height (' + this.state.unit + ')';
      s.slider.value = fmtDim(Math.max(this.state.w, this.state.h));

      s.lockBtn.innerHTML = this.state.locked ? iconLock() : iconUnlock();
      s.lockBtn.classList.toggle('is-locked', this.state.locked);
      s.lockBtn.setAttribute('aria-pressed', String(this.state.locked));
      s.lockBtn.title = this.state.locked
        ? 'Proportions locked — the crop keeps this shape. Unlock to reshape it.'
        : 'Proportions unlocked — drag the crop to reshape, and the size follows.';

      /* The readout always states the size in the OTHER unit, so both are on
         screen at once and neither has to be worked out. */
      /* What was typed, not the clamped size — under the minimum this would
         otherwise state 1 ft beside a field that says 6 in (see sizeError). */
      var RW = Math.max(0, Math.min(this.state.w, MAX_DIM)), RH = Math.max(0, Math.min(this.state.h, MAX_DIM));
      s.card.querySelector('[data-role="ftline"]').textContent = this.state.unit === 'ft'
        ? fmtDim(RW) + ' in × ' + fmtDim(RH) + ' in'
        : fmtFtInWords(RW) + ' × ' + fmtFtInWords(RH);

      s.card.querySelectorAll('.pricer-preset').forEach(function (b) {
        b.classList.toggle('is-on',
          Math.abs(W - parseFloat(b.dataset.w)) < 0.01 &&
          Math.abs(H - parseFloat(b.dataset.h)) < 0.01);
      });

      /* The "need something bigger" note (Colin, 2026-09-05). His pricing table
         stops at 30 ft, so past that we take it off-catalogue and route to a
         human rather than quoting a number we cannot stand behind.

         Shown when the customer is AT the ceiling, not only while they are
         typing past it — which is the whole point and was the bug in the first
         version. `state.oversize` is true only mid-keystroke and is cleared on
         focusout when the value clamps back to 360, so someone who dragged the
         slider to max, or typed 40 ft and tabbed away, ended up sitting at 30 ft
         with no explanation of why it stopped there.

         innerHTML rather than textContent because "Contact us" is a real link to
         the contact page, as asked. The markup is a fixed literal — no
         customer input reaches it. */
      var note = s.card.querySelector('[data-role="oversize"]');
      var atCeiling = this.state.oversize || W >= MAX_DIM || H >= MAX_DIM;
      if (atCeiling) {
        note.innerHTML = 'Need something bigger? <a href="' + esc(CONTACT_URL) + '">Contact us</a>' +
          ' and we’ll quote it for you.';
        note.classList.add('show');
        /* No "At 4 ft and under, our Custom Mylar Stencils may be the better
           fit" note below the ceiling any more — Colin asked for it removed,
           2026-10-04. */
      } else {
        note.classList.remove('show');
        /* Emptied, not just hidden. Leaving the previous message in the DOM
           meant a stale "Need something bigger?" sat inside a display:none
           element — invisible, but still real text that a screen reader in
           browse mode or any CSS override could surface at a size where it is
           untrue. innerHTML rather than textContent because the message can
           contain the Contact us <a>, and only this reliably drops it in both a
           real browser and the test DOM. */
        note.innerHTML = '';
      }

      /* Under the minimum (see sizeError). Next is held off the moment the
         size goes under, but the note waits until the entry is finished, so a
         "1" on the way to "15" doesn't flash a warning. It clears the instant
         the size is valid again, mid-typing or not. */
      var err = this.sizeError();
      var mylarSized = !!err && this.mylarSized();
      var minNote = s.card.querySelector('[data-role="minsize"]');
      if (minNote) {
        // Emptied, not just hidden — same reason as the oversize note above.
        if (!err) { minNote.classList.remove('show'); minNote.innerHTML = ''; }
        else if (!opts.typing || minNote.classList.contains('show')) {
          /* innerHTML for the links; err is escaped, the rest is a fixed
             literal, so no customer input reaches the markup. Up to 36" on both
             sides goes to Mylar. Under 2" with a side over 36" fits neither
             page (Mylar's floor is 2" too), so that one gets Contact us. */
          var top = this.state.unit === 'ft' ? fmtFtDec(MYLAR_TOP) + ' ft' : fmtDim(MYLAR_TOP) + '"';
          minNote.innerHTML = esc(err) + (mylarSized
            ? ' Up to ' + esc(top) + ' × ' + esc(top) + '? Order <a href="' + esc(MYLAR_URL) + '">Custom Mylar Stencils</a> instead.'
            : ' Need a narrower stencil? <a href="' + esc(CONTACT_URL) + '">Contact us</a> and we’ll quote it.');
          minNote.classList.add('show');
        }
        var flagged = minNote.classList.contains('show');
        [[s.inW, this.state.w], [s.inH, this.state.h]].forEach(function (p) {
          // A Mylar-sized entry outlines both sides: either one can fix it.
          var bad = flagged && (mylarSized || !(p[1] >= MIN_DIM));
          p[0].closest('.pricer-nudge').classList.toggle('is-invalid', bad);
          p[0].setAttribute('aria-invalid', String(bad));
        });
      }
      if (this._navNext) this._navNext.disabled = !!err;

      /* Rotate is meaningless on a square. */
      var rotate = s.card.querySelector('[data-role="rotate"]');
      if (rotate) rotate.classList.toggle('is-hidden', Math.abs(this.state.w / this.state.h - 1) < 0.02);

      this.updatePrice();

      /* A size change reshapes the crop; a crop drag must not reshape itself
         underneath the customer's finger, and neither must a unit toggle.
         Typing reshapes it live too, from the frame the entry started with.
         It used to wait, and the wait is what made the frame jump when the
         lock was tapped — see the lock button's handler. */
      if (!opts.fromCrop && s.hasArt) this.fitCropToRatio(opts.typing ? this._typeAnchor : null);
    }

    /* ==================================================================
       CROP — ported from pricer-v2.js. See CHANGELOG 2026-08-24 for why
       sizeCropImage sets explicit pixel dimensions in JS rather than CSS.
    ================================================================== */

    /* Sets an explicit pixel width/height on the crop image so it FILLS the
       card's content width. Without this the image drew at its natural pixel
       size — a 60×60px upload rendered as a 60×60 box with a crop frame too
       small to grab.

       .pricer-cropbox is absolutely positioned inside .pricer-cropwrap, and the
       wrapper is width:fit-content SPECIFICALLY so its box coincides with the
       image's — that coincidence is what keeps the crop frame registered over
       the artwork. Setting the image's width keeps the wrapper shrink-wrapped to
       it. Switching the CSS to width:100% on the wrapper instead would offset
       every crop coordinate. Do not "simplify" this. */
    sizeCropImage(){
      if (!this._size || !this._size.img) return;
      var img = this._size.img;
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return;
      var host = img.closest('.pricer-card');
      if (!host) return;
      var hcs = getComputedStyle(host);
      var avail = host.clientWidth
        - (parseFloat(hcs.paddingLeft) || 0)
        - (parseFloat(hcs.paddingRight) || 0);
      if (!(avail > 0)) return;
      var ics = getComputedStyle(img);
      /* The image's own border, cached here because paintCrop needs it on every
         frame of a drag and getComputedStyle in that loop would be a layout read
         per pointermove. See paintCrop for what it is for. */
      this._size.bl = parseFloat(ics.borderLeftWidth) || 0;
      this._size.bt = parseFloat(ics.borderTopWidth) || 0;
      /* Height is capped HERE, before the width is applied, rather than left to
         the CSS max-height: with an explicit width, max-height clamps the height
         without recomputing the width, which squashes the aspect ratio. Read off
         getComputedStyle so the CSS stays the single source of truth. */
      var maxH = parseFloat(ics.maxHeight);
      if (!Number.isFinite(maxH) || maxH <= 0) maxH = 440;
      var w = avail;
      if (w * (nh / nw) > maxH) w = maxH * (nw / nh);
      img.style.width = w + 'px';
      img.style.height = (w * nh / nw) + 'px';
      /* Stencil artwork is hard-edged black shapes; past 2× a blocky upscale
         reads as sharp rather than smeared. Nothing recovers detail the file
         never had — this only changes how the missing detail is drawn. */
      img.classList.toggle('is-upscaled', w / nw >= 2);
    }

    /* Reprojects the crop through FRACTIONS on resize/rotate, which are
       resolution-independent, so the box lands back over the same part of the
       artwork at the new size. */
    reflowCrop(){
      if (!this._size || !this._size.img || !this._size.hasArt || this.stepName() !== 'Size') return;
      var img = this._size.img;
      var W = img.clientWidth, H = img.clientHeight;
      var frac = (this.crop && W && H)
        ? { x: this.crop.x / W, y: this.crop.y / H, w: this.crop.w / W, h: this.crop.h / H }
        : null;
      this.sizeCropImage();
      var nW = img.clientWidth, nH = img.clientHeight;
      if (!nW || !nH) return;
      if (!frac) { this.initCrop(); return; }
      this.crop = { x: frac.x * nW, y: frac.y * nH, w: frac.w * nW, h: frac.h * nH };
      this.clampCrop();
      this.paintCrop();
    }

    initCrop(){
      if (!this._size || !this._size.img) return;
      this.sizeCropImage();
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (!W || !H) return;
      if (this.crop) { this.clampCrop(); this.paintCrop(); return; }
      if (this.state.savedCrop) {
        var c = this.state.savedCrop;
        this.crop = { x: c.x * W, y: c.y * H, w: c.w * W, h: c.h * H };
      } else {
        var r = this.state.w / this.state.h;
        var cw = W * 0.8, ch = cw / r;
        if (ch > H * 0.9) { ch = H * 0.9; cw = ch * r; }
        this.crop = { x: (W - cw) / 2, y: (H - ch) / 2, w: cw, h: ch };
      }
      this.clampCrop();
      this.paintCrop();
    }

    /* Zero-size guarded: tapping +/−, the slider or a preset before the image
       finished decoding used to run this against a zero-sized element and
       collapse the crop to 0×0 permanently, since initCrop only seeds a crop
       when there isn't one. Most likely on mobile with a large photo. */
    fitCropToRatio(from){
      if (!this._size || !this._size.img || !this.crop) return;
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (!W || !H) return;
      /* Clamped, because mid-typing a side can briefly be 0 or under the floor,
         and a 0 ratio would collapse the frame to nothing. Outside typing the
         sides are already clamped, so this is a no-op there. */
      var r = clampDim(this.state.w) / clampDim(this.state.h);
      if (from) { this.crop.x = from.x; this.crop.y = from.y; }
      var cw = (from || this.crop).w, ch = cw / r;
      /* Fit the frame INSIDE the stage rather than backing off to 90% of it.
         Backing off meant a frame that exactly filled the artwork lost a tenth
         of itself every time the size was nudged — so once you had framed the
         whole image, scaling it with the slider quietly walked the frame back
         off the edges again, which is half of why the whole image felt
         unreachable (Colin, 2026-09-06). Rounding alone can put ch a hair over
         H when the shapes match exactly, so this has to clamp, not shrink. */
      if (ch > H) { ch = H; cw = ch * r; }
      if (cw > W) { cw = W; ch = cw / r; }
      this.crop.w = cw; this.crop.h = ch;
      this.clampCrop();
      this.paintCrop();
    }

    clampCrop(){
      if (!this._size || !this._size.img || !this.crop) return;
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (!W || !H) return;
      var c = this.crop;
      c.w = Math.min(c.w, W);
      c.h = Math.min(c.h, H);
      c.x = Math.min(Math.max(0, c.x), W - c.w);
      c.y = Math.min(Math.max(0, c.y), H - c.h);
    }

    /* THE FRAME IS MEASURED AGAINST THE ARTWORK AND PAINTED AGAINST THE BORDER,
       so it needs the border added back on the way out.

       Crop coordinates live in the image's CONTENT box — everything measures
       against clientWidth/clientHeight, which excludes the image's 2px border.
       But .pricer-cropbox is absolutely positioned inside .pricer-cropwrap,
       whose content box coincides with the image's BORDER box. The theme is also
       box-sizing:border-box, so the width set here is the frame's outer edge,
       not its content.

       Left unadjusted, the two spaces are a border-width apart: a frame covering
       the whole artwork painted flush at the left and top but stopped **4px
       short of the right and bottom edges** — measured, not guessed. Colin spotted
       exactly that ("the crop box doesn't seem to go all the way to the outside",
       2026-09-07) on a build whose own test had passed it, because that test
       tolerated 4px. It no longer does. */
    paintCrop(){
      if (!this._size || !this._size.box || !this.crop) return;
      var b = this._size.box, c = this.crop;
      b.style.left = (c.x + (this._size.bl || 0)) + 'px';
      b.style.top = (c.y + (this._size.bt || 0)) + 'px';
      b.style.width = c.w + 'px';
      b.style.height = c.h + 'px';
    }

    onCropPointerDown(e){
      if (!this._size || !this._size.img || !this.crop) return;
      if (e.target.dataset.h) this.dragMode = e.target.dataset.h;
      else if (e.target === this._size.box) this.dragMode = 'move';
      else return;
      this.dragStart = {
        px: e.clientX, py: e.clientY,
        x: this.crop.x, y: this.crop.y, w: this.crop.w, h: this.crop.h,
        /* Inches per displayed pixel, frozen at gesture start, so a long drag
           converts consistently instead of compounding its own rounding. */
        kx: this.state.w / this.crop.w,
        ky: this.state.h / this.crop.h
      };
      e.preventDefault();
    }

    onCropPointerMove(e){
      if (!this.dragMode) return;
      if (!this._size || !this._size.img || !this.crop) { this.dragMode = null; return; }
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (!W || !H) return;
      var st = this.dragStart;
      var dx = e.clientX - st.px, dy = e.clientY - st.py;

      if (this.dragMode === 'move') {
        this.crop.x = st.x + dx;
        this.crop.y = st.y + dy;
        this.clampCrop();
        this.paintCrop();
      } else {
        var mode = this.dragMode;
        var maxW = (mode.indexOf('w') !== -1) ? st.x + st.w : W - st.x;
        var maxH = (mode.indexOf('n') !== -1) ? st.y + st.h : H - st.y;
        var ratio = this.state.locked ? (this.state.w / this.state.h) : null;
        var nw, nh;

        if (mode === 'se' || mode === 'ne') nw = Math.max(40, st.w + dx);
        else nw = Math.max(40, st.w - dx);

        if (ratio) {
          nw = Math.min(nw, maxW, maxH * ratio);
          nh = nw / ratio;
        } else {
          if (mode === 'se' || mode === 'sw') nh = Math.max(40, st.h + dy);
          else nh = Math.max(40, st.h - dy);
          nw = Math.min(nw, maxW);
          nh = Math.min(nh, maxH);
        }

        this.crop.w = nw; this.crop.h = nh;
        this.crop.x = (mode === 'nw' || mode === 'sw') ? st.x + st.w - nw : st.x;
        this.crop.y = (mode === 'nw' || mode === 'ne') ? st.y + st.h - nh : st.y;
        this.clampCrop();
        this.paintCrop();

        if (!this.state.locked) {
          var rawW = this.crop.w * st.kx, rawH = this.crop.h * st.ky;

          /* THE 30 FT CEILING BELONGS ON THE SIZE, NOT ON THE FRAME.
             (Colin, 2026-09-06: "I can't get it any bigger than this after
             playing around with it, but obviously I need to be able to go out
             to the whole image.")

             This used to clamp each side on its own and then shove the FRAME
             back to whatever fitted, which had two consequences, both visible
             in his screenshot of a design sitting at exactly 30 ft:
               - the frame could not be enlarged AT ALL any more. Every drag
                 outward was undone on the same tick, so there was no way to
                 frame the whole photo; and
               - because only the clamped side was pushed back, each rebound
                 skewed the frame narrower against the numbers, until a 30 ft ×
                 12 ft design was being drawn by a tall, narrow box.

             Scaling the pair down TOGETHER keeps the frame's shape and the
             numbers' shape the same thing, and lets the frame go wherever the
             customer drags it. The numbers stop at 30 ft, and paintSize's
             "Need something bigger? Contact us" note — which fires at the
             ceiling — is what explains why. */
          var fit = Math.min(1, MAX_DIM / rawW, MAX_DIM / rawH);
          if (fit < 1) { rawW *= fit; rawH *= fit; }

          this.state.w = snap4(clampDim(rawW));
          this.state.h = snap4(clampDim(rawH));
          /* The FLOOR still pushes the frame back, so the box and the numbers
             can't drift apart (they did: a portrait crop reading as near-square
             once both sides hit the floor). clampDim only ever RAISES a value
             at the floor, which is what this asymmetric test detects — the
             ceiling is handled above and must not land here. */
          if (this.state.w - rawW > 0.01 || this.state.h - rawH > 0.01) {
            var cw2 = this.state.w / st.kx, ch2 = this.state.h / st.ky;
            this.crop.x = (mode === 'nw' || mode === 'sw') ? this.crop.x + this.crop.w - cw2 : this.crop.x;
            this.crop.y = (mode === 'nw' || mode === 'ne') ? this.crop.y + this.crop.h - ch2 : this.crop.y;
            this.crop.w = cw2; this.crop.h = ch2;
            this.clampCrop();
            this.paintCrop();
          }
          this.paintSize({ fromCrop: true });
        }
      }
      if (e.cancelable) e.preventDefault();
    }

    /* Stores the crop as fractions of the full image and rebuilds the two
       derived artefacts. Called on EVERY exit from the Size card. */
    bakeCrop(){
      if (!this._size || !this._size.hasArt || !this._size.img || !this.crop) { this._size = null; return; }
      var W = this._size.img.clientWidth, H = this._size.img.clientHeight;
      if (W && H) {
        this.state.savedCrop = {
          x: this.crop.x / W, y: this.crop.y / H,
          w: this.crop.w / W, h: this.crop.h / H
        };
        this.refreshCroppedUpload();
      }
      this.crop = null;
      this._size = null;
    }

    openCropReview(){
      if (!this.el.cropModal || !this.state.fullSrc || !this.state.savedCrop) return;
      var c = this.state.savedCrop;
      var img = this.el.cropModalImg, box = this.el.cropModalBox;
      var place = function () {
        box.style.left = (c.x * 100) + '%';
        box.style.top = (c.y * 100) + '%';
        box.style.width = (c.w * 100) + '%';
        box.style.height = (c.h * 100) + '%';
      };
      if (img.src !== this.state.fullSrc) { img.onload = place; img.src = this.state.fullSrc; }
      else place();
      this.el.cropModal.classList.add('show');
      document.body.classList.add('pricer-lock');
    }

    /* ==================================================================
       STEP 3 · SHEETS — no mylar equivalent
    ================================================================== */
    renderSheets(card){
      var self = this;
      var sheetHtml = SHEETS.map(function (o) {
        return '<button type="button" class="pricer-sheet" data-sheet="' + esc(o.id) + '">' + esc(o.label) +
          '<span class="pc" data-role="pc"></span></button>';
      }).join('');

      card.innerHTML =
        '<h2 class="pricer-h2" data-role="h2">How should we split it?</h2>' +
        '<p class="pricer-sub" data-role="sub">Big stencils arrive in pieces that align at the seams.</p>' +
        '<div class="pricer-sheets" data-role="sheets">' + sheetHtml + '</div>' +
        '<div class="pricer-scalebox"><div data-role="preview"></div></div>' +
        '<p class="pricer-pieceline" data-role="pieceline"></p>';

      card.querySelectorAll('.pricer-sheet').forEach(function (b) {
        b.addEventListener('click', function () { self.state.sheetId = b.dataset.sheet; self.paintSheets(); });
      });

      this._sheets = { card: card };
      this.addNav(card, { backOk: true });
      this.paintSheets();
    }

    /* The chosen sheet's layout, or the best available one. Single source of
       truth for the step, the review row, the order properties and the tile map,
       so those four can never disagree. */
    currentLayout(){
      var W = clampDim(this.state.w), H = clampDim(this.state.h);
      var opt = null;
      for (var i = 0; i < SHEETS.length; i++) if (SHEETS[i].id === this.state.sheetId) opt = SHEETS[i];
      if (!opt) opt = SHEETS[0];
      if (!opt) return null;
      var layout = tile(W, H, opt.sheetW, opt.sheetH);
      return {
        opt: opt,
        layout: layout,
        /* Equal division rather than full sheets plus a remainder: every piece
           the same size means the seams line up in a regular grid, which is what
           the split preview draws and what the customer is agreeing to.

           NOT snapped to a quarter inch, deliberately. The ORDERED size is on a
           quarter inch; an equal division of it usually is not, and rounding
           each piece to the nearest quarter made the pieces stop summing to the
           stencil — 110" over 3 pieces reported as 36.75" each, i.e. 110.25", on
           71% of all orderable sizes (993 of 1393 quarter-inch steps, measured).
           The floor would cut a quarter inch too much, or notice the arithmetic
           does not close and stop to ask. Carry the true value and let fmtDim
           round it for display only. */
        pieceW: W / layout.cols,
        pieceH: H / layout.rows
      };
    }

    paintSheets(){
      if (!this._sheets || this.stepName() !== 'Sheets') return;
      var card = this._sheets.card;
      var W = clampDim(this.state.w), H = clampDim(this.state.h);

      var layouts = {};
      SHEETS.forEach(function (o) { layouts[o.id] = tile(W, H, o.sheetW, o.sheetH); });
      var std = layouts.standard, big = layouts.jumbo;

      /* When large sheets can't save any pieces, standard is strictly better
         (same count, ships flat), so there is no decision to make. The step
         stays — the split preview is still the point — but it tells rather than
         asks, so the customer doesn't feel a step got skipped. */
      var noChoice = !std || !big || big.pieces >= std.pieces;
      if (noChoice && std) this.state.sheetId = 'standard';
      card.querySelector('[data-role="sheets"]').style.display = noChoice ? 'none' : '';

      var h2 = card.querySelector('[data-role="h2"]');
      var sub = card.querySelector('[data-role="sub"]');
      if (!noChoice) {
        h2.textContent = 'How should we split it?';
        sub.textContent = 'Big stencils arrive in pieces that align at the seams.';
      } else if (std && std.pieces === 1) {
        h2.textContent = 'Fits on one sheet';
        sub.textContent = 'No seams, no assembly — it ships flat in a box.';
      } else if (std) {
        h2.textContent = 'Here’s how it splits';
        sub.textContent = std.pieces + ' pieces that align at the seams, cut on standard sheets so it ships flat. Larger sheets wouldn’t reduce the count at this size.';
      }

      var cur = this.currentLayout();
      if (!cur) return;

      card.querySelectorAll('.pricer-sheet').forEach(function (b) {
        b.classList.toggle('is-on', b.dataset.sheet === this.state.sheetId);
        var l = layouts[b.dataset.sheet];
        if (l) b.querySelector('[data-role="pc"]').textContent = l.pieces === 1 ? '1 piece' : l.pieces + ' pieces';
      }, this);

      var drawn = this.drawSplit(card.querySelector('[data-role="preview"]'), W, H, cur);

      var pieceline = card.querySelector('[data-role="pieceline"]');
      if (cur.layout.pieces === 1) {
        pieceline.innerHTML = '<b>1 piece</b> — no assembly needed — ' + esc(cur.opt.ship);
      } else {
        /* When the pieces are drawn too small to label individually, the size
           goes HERE rather than being painted across the middle of the grid,
           where it landed on top of the sheets and was unreadable (seen in
           Colin's 10 × 10 ft screenshot, 2026-09-04). */
        pieceline.innerHTML = '<b>' + cur.layout.pieces + ' pieces</b>' +
          (drawn.labelledInPlace ? '' : ', each <b>' + fmtDim(cur.pieceW) + '" × ' + fmtDim(cur.pieceH) + '"</b>') +
          ' — ' + esc(cur.opt.ship);
      }

      this.updatePrice();
    }

    /* The stencil drawn as its separate pieces: grey sheets with a light border,
       a gap at each seam, per-piece dims centred on each piece when they fit,
       and the overall size called out in feet and inches.

       Returns { labelledInPlace } so the caller knows whether the per-piece size
       still needs saying in words underneath. */
    drawSplit(elp, W, H, cur){
      if (!elp) return { labelledInPlace: false };
      var layout = cur.layout;
      var VW = 360, VH = 176;
      var padL = 40, padR = 12, padT = 26, padB = 8;
      var gap = 4;

      var availW = VW - padL - padR - (layout.cols - 1) * gap;
      var availH = VH - padT - padB - (layout.rows - 1) * gap;
      var ppi = Math.min(availW / W, availH / H);

      var sw = W * ppi + (layout.cols - 1) * gap;
      var sh = H * ppi + (layout.rows - 1) * gap;
      var sx = padL + (VW - padL - padR - sw) / 2;
      var sy = padT + (VH - padT - padB - sh) / 2;

      var pw = (W * ppi) / layout.cols, ph = (H * ppi) / layout.rows;
      var showDims = pw >= 44 && ph >= 34;

      var pieces = '';
      for (var r = 0; r < layout.rows; r++) {
        for (var c = 0; c < layout.cols; c++) {
          var x = sx + c * (pw + gap);
          var y = sy + r * (ph + gap);
          pieces += '<rect x="' + x + '" y="' + y + '" width="' + pw + '" height="' + ph +
            '" rx="2" fill="#eceae5" stroke="#b5b2a9" stroke-width="1.25"/>';
          if (showDims) {
            var cx = x + pw / 2, cy = y + ph / 2;
            pieces += '<text x="' + cx + '" y="' + (cy - 2.5) +
              '" text-anchor="middle" font-size="10" font-weight="600" fill="#6c6a63">' +
              fmtDim(cur.pieceW) + '"</text>' +
              '<text x="' + cx + '" y="' + (cy + 9.5) +
              '" text-anchor="middle" font-size="10" font-weight="600" fill="#6c6a63">' +
              '× ' + fmtDim(cur.pieceH) + '"</text>';
          }
        }
      }

      var dims =
        '<g stroke="#141414" stroke-width="1">' +
          '<line x1="' + sx + '" y1="' + (sy - 11) + '" x2="' + (sx + sw) + '" y2="' + (sy - 11) + '"/>' +
          '<line x1="' + sx + '" y1="' + (sy - 15) + '" x2="' + sx + '" y2="' + (sy - 7) + '"/>' +
          '<line x1="' + (sx + sw) + '" y1="' + (sy - 15) + '" x2="' + (sx + sw) + '" y2="' + (sy - 7) + '"/>' +
          '<line x1="' + (sx - 11) + '" y1="' + sy + '" x2="' + (sx - 11) + '" y2="' + (sy + sh) + '"/>' +
          '<line x1="' + (sx - 15) + '" y1="' + sy + '" x2="' + (sx - 7) + '" y2="' + sy + '"/>' +
          '<line x1="' + (sx - 15) + '" y1="' + (sy + sh) + '" x2="' + (sx - 7) + '" y2="' + (sy + sh) + '"/>' +
        '</g>' +
        '<text x="' + (sx + sw / 2) + '" y="' + (sy - 17) + '" text-anchor="middle" font-size="11" font-weight="600" fill="#141414">' + fmtFtIn(W) + '</text>' +
        '<text x="' + (sx - 19) + '" y="' + (sy + sh / 2) + '" text-anchor="middle" font-size="11" font-weight="600" fill="#141414" transform="rotate(-90 ' + (sx - 19) + ' ' + (sy + sh / 2) + ')">' + fmtFtIn(H) + '</text>';

      elp.innerHTML =
        '<svg viewBox="0 0 ' + VW + ' ' + VH + '" xmlns="http://www.w3.org/2000/svg" role="img" ' +
          'aria-label="' + fmtDim(W) + ' by ' + fmtDim(H) + ' inch stencil in ' + layout.pieces + ' pieces">' +
          pieces + dims +
        '</svg>';

      return { labelledInPlace: showDims };
    }

    /* ==================================================================
       STEP 4 · REVIEW
    ================================================================== */
    renderDetails(card){
      var self = this;
      var uid = this.dataset.uid || 'lg';
      var W = clampDim(this.state.w), H = clampDim(this.state.h);
      var cur = this.currentLayout();

      var artCell;
      if (this.state.artUploaded && (this._croppedThumb || this.state.fullSrc)) {
        /* A button, not an image: tapping it opens the read-only crop review, so
           the customer can confirm what is actually being made. */
        artCell = '<button type="button" class="pricer-thumb" data-role="thumb" aria-label="See your crop on the full image">' +
          '<img src="' + esc(this._croppedThumb || this.state.fullSrc) + '" alt="Your design"></button>';
      } else if (this.state.artUploaded) {
        artCell = '<span>' + esc(this._fileName || 'Your file') + '</span>';
      } else {
        artCell = '<span>Emailing after ordering</span>';
      }

      var sheetRow = cur
        ? '<div class="row"><span class="k">Sheets</span><span class="v">' +
            (cur.layout.pieces === 1 ? '1 piece' : cur.layout.pieces + ' pieces · ' + fmtDim(cur.pieceW) + '" × ' + fmtDim(cur.pieceH) + '"') +
            '<button type="button" class="pricer-editlink" data-go="2">Edit</button></span></div>'
        : '';

      card.innerHTML =
        '<h2 class="pricer-h2">Review</h2>' +   // matches the bar + button (Colin, 2026-10-14: "they all need to match exactly")
        '<p class="pricer-sub">Check your order and tell us about your project.</p>' +
        '<div class="pricer-review">' +
          '<div class="row"><span class="k">Design</span><span class="v">' + artCell + '</span></div>' +
          '<div class="row"><span class="k">Size</span><span class="v">' + this.fmtSizeChosen(W, H) +
            '<button type="button" class="pricer-editlink" data-go="1">Edit</button></span></div>' +
          sheetRow +
          '<div class="row"><span class="k">Quantity</span><span class="v v--stack">' +
            '<span class="pricer-qmini">' +
              '<button type="button" data-role="qMinus" aria-label="Decrease quantity">−</button>' +
              '<span data-role="qVal">' + this.state.qty + '</span>' +
              '<button type="button" data-role="qPlus" aria-label="Increase quantity">+</button>' +
            '</span>' +
            '<span class="pricer-qnudge" data-role="qNudge"></span>' +
          '</span></div>' +
          '<div class="row" data-role="eachrow"><span class="k">Per stencil</span><span data-role="each">—</span></div>' +
          '<div class="row"><span class="k">Total</span><span class="pricer-reviewtotal" data-role="rtotal">—</span></div>' +
        '</div>' +
        '<button type="button" class="pricer-reflink" data-role="detailstoggle">+ Add project details (optional)</button>' +
        '<div class="pricer-details" data-role="details">' +
          '<div class="pricer-field">' +
            '<label for="pricer-lg-notes-' + uid + '">Tell us about your project</label>' +
            '<textarea id="pricer-lg-notes-' + uid + '" data-role="pinfo" name="properties[𝗣𝗿𝗼𝗷𝗲𝗰𝘁 𝗗𝗲𝘁𝗮𝗶𝗹𝘀]" placeholder="Surface, paint, deadline — whatever helps"></textarea>' +
          '</div>' +
        '</div>';

      var details = card.querySelector('[data-role="details"]');
      var toggle = card.querySelector('[data-role="detailstoggle"]');
      var notes = card.querySelector('[data-role="pinfo"]');
      /* Value is mirrored on the instance, not read back off the node — the card
         is destroyed on every render, so anything living only in the DOM is lost
         the moment the customer steps back. */
      notes.value = this._pinfo || '';
      var open = !!notes.value;
      var paintToggle = function () {
        details.classList.toggle('show', open);
        toggle.textContent = open ? '− Hide project details' : '+ Add project details (optional)';
      };
      toggle.addEventListener('click', function () { open = !open; paintToggle(); });
      paintToggle();
      notes.addEventListener('input', function () { self._pinfo = notes.value; });

      card.querySelectorAll('.pricer-editlink').forEach(function (b) {
        b.addEventListener('click', function () { self.jumpTo(parseInt(b.dataset.go, 10)); });
      });
      var thumb = card.querySelector('[data-role="thumb"]');
      if (thumb) thumb.addEventListener('click', function () { self.openCropReview(); });

      var applyQty = function (v) {
        self.state.qty = Math.min(MAX_QTY, Math.max(1, Math.round(v)));
        self.paintDetails();
      };
      card.querySelector('[data-role="qMinus"]').addEventListener('click', function () { applyQty(self.state.qty - 1); });
      card.querySelector('[data-role="qPlus"]').addEventListener('click', function () { applyQty(self.state.qty + 1); });

      this._details = { card: card };
      /* The real Add to Cart button is MOVED in from the stash, never recreated
         — handleAddToCart drives its label through a multi-second async sequence
         that a per-render button could not survive. */
      this.addNav(card, { backOk: true, node: this.el.atcBtn });
      card.appendChild(this.el.atcNote);
      this.paintDetails();
    }

    paintDetails(){
      if (!this._details || this.stepName() !== 'Details') return;
      var card = this._details.card;
      /* "Each" is the order total divided by the quantity — the sheet prices a
         whole order, so there is no separate per-unit number to discount. */
      var eachDiscounted = priceEach(this.state.w, this.state.h, this.state.qty);
      var total = priceTotal(this.state.w, this.state.h, this.state.qty);

      card.querySelector('[data-role="qVal"]').textContent = this.state.qty;
      card.querySelector('[data-role="qMinus"]').disabled = this.state.qty <= 1;
      card.querySelector('[data-role="each"]').textContent = eachDiscounted == null ? '—' : money(eachDiscounted);
      // Redundant with Total at qty 1.
      card.querySelector('[data-role="eachrow"]').style.display = this.state.qty > 1 ? '' : 'none';
      card.querySelector('[data-role="rtotal"]').textContent = total == null ? '—' : money(total);

      /* The one place the volume tiers surface for large — most orders are qty 1
         and a full savings table would be noise. */
      var next = nextBreak(this.state.w, this.state.h, this.state.qty);
      var nudge = card.querySelector('[data-role="qNudge"]');
      if (next) {
        var need = next.qty - this.state.qty;
        nudge.textContent = 'Add ' + need + ' and save ' + next.pct + '%';
      } else {
        var saved = savedPct(this.state.w, this.state.h, this.state.qty);
        nudge.textContent = saved ? saved + '% off applied' : '';
      }

      this.updatePrice();
    }

    /* ==================================================================
       PRICE + GATE
    ================================================================== */
    updatePrice(){
      var card = this.el.cardhost.firstChild;
      var totalEl = card && card.querySelector('[data-role="cardtotal"]');
      if (totalEl) {
        // No price for an under-minimum size: the table would quietly price it as the minimum.
        var t = this.sizeError() ? null : priceTotal(this.state.w, this.state.h, this.state.qty);
        totalEl.textContent = t == null ? '—' : money(t);
      }
      var qtyEl = card && card.querySelector('[data-role="cardqty"]');
      if (qtyEl) {
        qtyEl.textContent = this.state.qty > 1
          ? 'for ' + this.state.qty + ' stencils' : '';
      }
      this.refreshGate();
    }

    refreshGate(){
      var artworkChosen = this.state.artUploaded || this.state.sendLater;
      var needsAck = this.state.artUploaded && !this.state.previewable;
      var acked = !needsAck || (this.el.noPreviewAck && this.el.noPreviewAck.checked);
      var priced = !this.sizeError() && priceTotal(this.state.w, this.state.h, this.state.qty) != null;

      /* THE PLACEHOLDER GATE. While assets/pricer-large-data.js still carries
         `placeholder: true` the numbers on screen are Colin's prototype
         placeholders, not a real quote, and the backend calculator would refuse
         these sizes anyway. Selling at a made-up price is the one failure mode
         this whole architecture exists to prevent, so the sale is blocked here,
         in the open, rather than dying in a failed fetch at the last step.
         DELETE the flag in the data file when the real large table lands. */
      var placeholder = !!(DATA && DATA.placeholder);

      this.el.atcBtn.disabled = !artworkChosen || !acked || !priced || placeholder;

      /* The placeholder message used to carry a phone number lifted straight
         from Colin's prototype. It was his personal mobile — "I would never
         want my personal number on the website" (2026-09-06). It routes to the
         contact page instead, which is the same route the "Need something
         bigger?" note already takes and the one the store actually staffs.
         Nothing on this page should publish a number that isn't the store's.

         innerHTML only on that one branch, and only over a fixed literal plus
         the escaped CONTACT_URL — the other branches interpolate
         state.uploadError and stay on textContent. */
      if (!artworkChosen) {
        this.el.atcNote.textContent = this.state.uploadError || 'Choose how you’ll send your artwork to continue';
      } else if (!acked) {
        this.el.atcNote.textContent = 'Confirm the note above so we know how to proof your file';
      } else if (!priced) {
        this.el.atcNote.textContent = 'Pick a size to see your price';
      } else if (placeholder) {
        /* Says nothing about pricing being unfinished — Colin, 2026-09-07: "you
           can remove that whole line about the pricing being finalized". The
           GATE is unchanged and still blocks the sale; only the admission is
           gone. It now reads as a quote route, which is true at any size while
           the table is a placeholder. Both this note and the disabled button
           disappear on their own the moment `placeholder` leaves the data file —
           there is nothing here to remember to take out. */
        this.el.atcNote.innerHTML = '<a href="' + esc(CONTACT_URL) + '">Contact us</a>' +
          ' for a quote on this size.';
      } else {
        this.el.atcNote.textContent = '';
      }

      if (this.stepName() === 'Upload') this.paintUpload();
    }

    /* ==================================================================
       ARTWORK
    ================================================================== */
    onFileChosen(f){
      if (!f) return;
      var self = this;

      /* Shopify rejects line-item property files over 20MB. Caught here with a
         clear message rather than letting Add to Cart fail at the last step,
         after the customer has configured everything. Absent from the prototype,
         which declared an uploadError field and never set it. */
      if (f.size > MAX_UPLOAD_BYTES) {
        this.el.fileInput.value = '';
        this.clearArtwork();
        this.state.uploadError = 'That file is ' + (f.size / 1048576).toFixed(0) +
          'MB. Files over 20MB can’t be attached to an order — send a smaller version, or choose "Email it after ordering" below.';
        this.paintUpload();
        this.refreshGate();
        return;
      }

      /* Kept so the designer SVG can size its embedded copy against what the
         original is already costing this request — see encodeArtworkForSvg. */
      this._fileBytes = f.size;

      this.state.uploadError = null;
      this.state.artUploaded = true;
      this.state.sendLater = false;
      this.state.pausedUpload = null;
      /* Clear the OLD file's measurements too — leaving them meant the shape
         check ran against the previous artwork until the new one loaded. */
      this.state.artW = null; this.state.artH = null; this.state.fullSrc = null;
      this.state.previewable = false; this.state.savedCrop = null;
      this.state.fitMode = null;
      this.crop = null;
      this._croppedThumb = null;
      if (this.el.croppedInput) this.el.croppedInput.value = '';
      if (this.el.designerInput) this.el.designerInput.value = '';
      if (this.el.noPreviewAck) this.el.noPreviewAck.checked = false;

      this._fileName = f.name;
      this._fileStatus = 'Reading your file…';
      if (!this._dname) this._dname = f.name.replace(/\.[^.]+$/, '');
      this.paintUpload();

      if (f.type.indexOf('image/') === 0) {
        var fr = new FileReader();
        fr.onload = function () {
          var img = new Image();
          img.onload = function () {
            self.state.artW = img.naturalWidth;
            self.state.artH = img.naturalHeight;
            self.state.fullSrc = fr.result;
            self.state.previewable = true;
            self._fileStatus = null;   // the thumbnail is the confirmation
            self.paintUpload();
            self.refreshGate();
            self.autoAdvance();
          };
          img.onerror = function () { self.markUnpreviewable(); };
          img.src = fr.result;
        };
        fr.onerror = function () { self.markUnpreviewable(); };
        fr.readAsDataURL(f);
      } else if (isPdfLike(f)) {
        this._fileStatus = 'Converting your file…';
        this.paintUpload();
        this.renderPdfPreview(f);
      } else {
        /* The prototype dropped anything that wasn't an image SILENTLY — no
           message, nothing happened — while advertising AI, EPS, PDF, PSD and
           TIFF on the button. Same silent-rejection shape as the old iPhone
           upload bug. EPS and PSD genuinely can't be rasterised in a browser, so
           they take the acknowledged path rather than vanishing. */
        this.markUnpreviewable();
      }
    }

    markUnpreviewable(){
      this.state.artW = null; this.state.artH = null;
      this.state.fullSrc = null; this.state.previewable = false;
      this._fileStatus = '✓ Uploaded — we’ll check it by hand';
      this.paintUpload();
      this.refreshGate();
      /* NO autoAdvance() here, deliberately — and this is the one place the
         prototype got it wrong. It advanced from here too, which carried the
         customer straight past the acknowledgement tickbox that this path exists
         to show them. autoAdvance() is gated on canLeaveUpload() so it would
         refuse anyway; not calling it at all makes the intent explicit. */
    }

    /* Colin's prototype moves to Size the moment a file is read, rather than
       making the customer press Next on a step whose job is now done — "a
       finished upload is the customer saying next" (large-pricer-preview.html).
       Kept, because it is his design; gated on canLeaveUpload() so a file that
       still needs the no-preview acknowledgement cannot be skipped past. */
    autoAdvance(){
      if (this.stepName() === 'Upload' && this.canLeaveUpload()) this.goNext();
    }

    /* Rasterises page 1 so the artwork can be measured and cropped like any
       image. PDF.js is ~370KB so it is only fetched once a PDF or .ai is
       actually chosen, never on page load. */
    async renderPdfPreview(f){
      var self = this;
      try {
        var lib = await loadPdfJs(this.dataset.pdfjs, this.dataset.pdfjsWorker);
        var buf = await f.arrayBuffer();
        var doc = await lib.getDocument({ data: buf }).promise;
        var page = await doc.getPage(1);
        var base = page.getViewport({ scale: 1 });
        /* Cap the raster so a large-format PDF doesn't blow up memory, but keep
           it detailed enough to crop against. Large stencils are commonly
           supplied as big vector files, so this path matters more here than on
           mylar. */
        var scale = Math.min(2, 1600 / Math.max(base.width, base.height));
        var vp = page.getViewport({ scale: scale });
        var cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(vp.width));
        cv.height = Math.max(1, Math.round(vp.height));
        await page.render({ canvasContext: cv.getContext('2d'), viewport: vp }).promise;

        self.state.artW = cv.width;
        self.state.artH = cv.height;
        self.state.fullSrc = cv.toDataURL('image/png');
        self.state.previewable = true;
        self._fileStatus = null;
        self.paintUpload();
        self.refreshGate();
        /* Same as the image path: a rasterised PDF/.ai is a finished, croppable
           upload, so it advances too. */
        self.autoAdvance();
      } catch (err) {
        /* Encrypted, corrupt, or a non-PDF-compatible .ai — fall back to the
           acknowledged path rather than leaving the customer stuck. */
        self.markUnpreviewable();
      }
    }

    /* Renders exactly what the customer selected — the crop applied to the
       artwork — and uploads it alongside the original file. Crop coordinates on
       their own are unusable by a designer; this is the selection as a picture.

       Returns a Promise that resolves once croppedInput.files is actually set.
       Tracked on _artworkReady as well as returned, because callers vary in
       whether they await it — Add to Cart is the one place that MUST wait, and
       it does so regardless of which caller started the rebuild. */
    refreshCroppedUpload(){
      var self = this;
      if (!this.state.fullSrc || !this.el.croppedInput) return Promise.resolve();
      var promise = new Promise(function (resolve) {
        var im = new Image();
        im.onload = function () {
          var s = self.state.savedCrop || { x: 0, y: 0, w: 1, h: 1 };
          var sw = Math.max(1, Math.round(s.w * im.naturalWidth));
          var sh = Math.max(1, Math.round(s.h * im.naturalHeight));
          /* Cap the long edge — a full-resolution crop of a large scan would
             balloon the upload for no benefit; the original carries the detail. */
          var scale = Math.min(1, 2000 / Math.max(sw, sh));
          var cv = document.createElement('canvas');
          cv.width = Math.max(1, Math.round(sw * scale));
          cv.height = Math.max(1, Math.round(sh * scale));
          cv.getContext('2d').drawImage(
            im, s.x * im.naturalWidth, s.y * im.naturalHeight, sw, sh, 0, 0, cv.width, cv.height
          );
          self._croppedThumb = cv.toDataURL('image/png');

          /* Built from the same loaded image and the same crop fractions, in the
             same pass, so the frame can never describe a different crop than the
             preview does. Rides inside this promise deliberately — _artworkReady
             then gates Add to Cart on the SVG too. */
          self.attachDesignerSvg(im, s);
          self.attachTileMap();
          self._svgStale = false;

          if (!cv.toBlob) { resolve(); return; }
          cv.toBlob(function (blob) {
            if (!blob) { resolve(); return; }
            try {
              var dt = new DataTransfer();
              dt.items.add(new File([blob], 'cropped-artwork.png', { type: 'image/png' }));
              self.el.croppedInput.files = dt.files;
            } catch (e) {
              /* Older browsers can't build a File list programmatically. The
                 original file and the crop still go through. */
            }
            resolve();
          }, 'image/png');
        };
        im.onerror = function () { resolve(); };   // don't hang the caller on a bad fullSrc
        im.src = self.state.fullSrc;
      });
      this._artworkReady = promise;
      return promise;
    }

    /* ------------------------------------------------------------------
       DESIGNER SVG — one self-contained file replacing the designer's need
       to guess which of two near-identical attachments is the source.

       Two top-level groups, because Illustrator maps them to layers: the
       artwork, and a crop frame the designer hides or deletes. The file declares
       its size in INCHES such that the crop region measures the ordered size on
       the artboard — open it and the frame is already at production scale.

       Colin, 2026-08-20: the frame measures the DESIGN (the priced width ×
       height), not the sheet with its border. Designers are on current
       Illustrator CC.

       NOTE FOR LARGE: Illustrator's artboard ceiling is 227.54", and a large
       stencil is routinely bigger than that on its own — a 30 ft side is 360".
       So unlike mylar, the scale-down path below is the NORMAL case here, not an
       edge case, and essentially every large order's SVG will carry the "NOT AT
       SIZE" note. That is correct and intended: a file that opens with its scale
       stated beats a file that will not open.
    ------------------------------------------------------------------ */

    /* The SVG is the THIRD file in the same cart POST, behind the original and
       the cropped preview. Shopify's 20MB is per file, but the request still has
       to complete on a phone connection — so the budget shrinks as the original
       grows rather than being a flat number that happens to pass on a small
       upload and stalls checkout on a big one. */
    svgArtworkBudget(){
      var spent = this._fileBytes || 0;
      return Math.max(3 * 1024 * 1024, Math.min(SVG_ARTWORK_BUDGET, 18 * 1024 * 1024 - spent));
    }

    /* The embedded raster is state.fullSrc — the canvas re-encode, NOT the
       customer's original bytes. Deliberate on two counts:

       1. Orientation. A phone photo carries EXIF rotation. Canvas decoding
          applies it; SVG <image> does not. Embedding the raw file would open
          sideways in Illustrator with the frame over the wrong region — and
          savedCrop's fractions are measured against the ORIENTED image, so
          artwork and frame would disagree.
       2. Size. The original may be 20MB; base64 adds a third on top, and this
          rides alongside two other attachments.

       Falls back to JPEG, then to progressively smaller JPEGs, when the PNG
       blows the budget. Returns null if even the smallest won't fit, and the
       caller then simply doesn't attach an SVG. */
    encodeArtworkForSvg(im){
      var budget = this.svgArtworkBudget();
      if (this.state.fullSrc && this.state.fullSrc.length <= budget) {
        return this.state.fullSrc;
      }
      var natW = im.naturalWidth, natH = im.naturalHeight;
      var scale = 1;
      for (var i = 0; i < 5; i++) {
        var cv = document.createElement('canvas');
        cv.width = Math.max(1, Math.round(natW * scale));
        cv.height = Math.max(1, Math.round(natH * scale));
        var ctx = cv.getContext('2d');
        /* JPEG has no alpha. Without this, a transparent PNG or a cut-out logo
           bakes to solid black and the designer opens a black square. */
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.drawImage(im, 0, 0, cv.width, cv.height);
        var href;
        try { href = cv.toDataURL('image/jpeg', 0.92); } catch (e) { return null; }
        if (href.length <= budget) return href;
        scale *= 0.7;
      }
      return null;
    }

    buildDesignerSvg(im, s){
      var natW = im.naturalWidth, natH = im.naturalHeight;
      if (!natW || !natH || !s.w || !s.h) return null;

      var href = this.encodeArtworkForSvg(im);
      if (!href) return null;

      var cx = s.x * natW, cy = s.y * natH;
      var cw = s.w * natW, ch = s.h * natH;

      /* The whole point: declare the page in inches so that the CROP measures
         the ordered size. Full image inches = ordered ÷ the crop's fraction of
         it. Four decimals — rounding to two would put the frame out by a few
         thousandths for no benefit. */
      var inW = this.state.w / s.w;
      var inH = this.state.h / s.h;

      var MAX_ARTBOARD_IN = 220;
      var over = Math.max(inW, inH) / MAX_ARTBOARD_IN;
      var scaleNote = '';
      if (over > 1) {
        inW /= over;
        inH /= over;
        scaleNote = ' · NOT AT SIZE — shown at 1:' + (Math.round(over * 1000) / 1000) +
          ', cut to the stated size above';
      }
      inW = Math.round(inW * 10000) / 10000;
      inH = Math.round(inH * 10000) / 10000;

      /* Scale the frame furniture to the image so it is neither invisible on a
         400px upload nor comically thick on an 8000px one. */
      var sw = Math.max(2, natW * 0.004);
      var fs = Math.max(14, natW * 0.02);

      /* Above the frame normally; inside it when the crop is hard against the
         top edge, where an outside label would sit off the artboard. */
      var labelY = (cy - sw * 2 >= fs * 1.2) ? (cy - sw * 2) : (cy + fs * 1.4);

      var cur = this.currentLayout();
      var label = fmtDim(this.state.w) + '" × ' + fmtDim(this.state.h) + '"' +
        ' · qty ' + this.state.qty +
        (cur ? ' · ' + cur.layout.pieces + (cur.layout.pieces === 1 ? ' piece' : ' pieces') +
               ' @ ' + fmtDim(cur.pieceW) + '"×' + fmtDim(cur.pieceH) + '"' : '') +
        (this._dname ? ' · ' + this._dname : '') + scaleNote;

      /* Text-node escaping, not attribute escaping. esc() also converts quotes
         to &quot;, which is valid XML but leaves the inch marks written as
         entities in a file a human opens and reads. Only & and < can break a
         text node. */
      var labelXml = String(label).replace(/&/g, '&amp;').replace(/</g, '&lt;');

      return '<?xml version="1.0" encoding="UTF-8"?>\n' +
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"\n' +
        '     width="' + inW + 'in" height="' + inH + 'in" viewBox="0 0 ' + natW + ' ' + natH + '">\n' +
        '  <g id="artwork">\n' +
        '    <image xlink:href="' + href + '" x="0" y="0" width="' + natW + '" height="' + natH + '"/>\n' +
        '  </g>\n' +
        '  <g id="CROP-FRAME--DO-NOT-PRINT">\n' +
        '    <path d="M0,0 H' + natW + ' V' + natH + ' H0 Z M' + cx + ',' + cy +
                 ' V' + (cy + ch) + ' H' + (cx + cw) + ' V' + cy + ' Z"\n' +
        '          fill="#141414" fill-opacity="0.45" fill-rule="evenodd"/>\n' +
        '    <rect x="' + cx + '" y="' + cy + '" width="' + cw + '" height="' + ch + '"\n' +
        '          fill="none" stroke="#FFDE1B" stroke-width="' + sw + '"/>\n' +
        '    <text x="' + (cx + sw * 2) + '" y="' + labelY + '"\n' +
        '          font-family="Arial, Helvetica, sans-serif" font-size="' + fs + '" font-weight="bold"\n' +
        '          fill="#FFDE1B" stroke="#141414" stroke-width="' + (fs * 0.06) + '" paint-order="stroke">' +
                 labelXml + '</text>\n' +
        '  </g>\n' +
        '</svg>\n';
    }

    attachDesignerSvg(im, s){
      if (!this.el.designerInput) return;
      var svg;
      try { svg = this.buildDesignerSvg(im, s); } catch (e) { svg = null; }
      if (!svg) { this.el.designerInput.value = ''; return; }
      try {
        var name = (this._dname || 'stencil-order')
          .replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'stencil-order';
        var dt = new DataTransfer();
        dt.items.add(new File([svg], name + '-designer.svg', { type: 'image/svg+xml' }));
        this.el.designerInput.files = dt.files;
      } catch (e) {
        /* Older browsers can't build a FileList. The original and the cropped
           preview still go through — this file is an addition, never a
           replacement, so losing it costs the designer convenience, not data. */
        this.el.designerInput.value = '';
      }
    }

    /* ------------------------------------------------------------------
       TILE MAP — the split, as a file, for the floor. No mylar equivalent,
       because mylar is always one piece.

       Drawn at 1/12 scale (1 inch on the page = 1 foot of stencil) so a 30 ft
       stencil is a 30 inch page that opens and prints. Every piece carries its
       row/column index and its real cut dimensions, so a stack of cut sheets can
       be matched back to the diagram without measuring.
    ------------------------------------------------------------------ */
    buildTileMapSvg(){
      var cur = this.currentLayout();
      if (!cur) return null;
      var W = clampDim(this.state.w), H = clampDim(this.state.h);
      var layout = cur.layout;

      var SC = 1 / 12;                      // page inches per stencil inch
      var pad = 1.2;                        // page inches of margin
      var pageW = W * SC + pad * 2;
      var pageH = H * SC + pad * 2.6;       // extra at the top for the header

      var pw = (W * SC) / layout.cols;
      var ph = (H * SC) / layout.rows;

      var body = '';
      for (var r = 0; r < layout.rows; r++) {
        for (var c = 0; c < layout.cols; c++) {
          var x = pad + c * pw, y = pad * 1.6 + r * ph;
          body += '<rect x="' + x + '" y="' + y + '" width="' + pw + '" height="' + ph +
            '" fill="none" stroke="#141414" stroke-width="0.012"/>' +
            '<text x="' + (x + pw / 2) + '" y="' + (y + ph / 2 - 0.06) +
            '" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="0.18" font-weight="bold" fill="#141414">' +
            'R' + (r + 1) + 'C' + (c + 1) + '</text>' +
            '<text x="' + (x + pw / 2) + '" y="' + (y + ph / 2 + 0.16) +
            '" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="0.13" fill="#141414">' +
            fmtDim(cur.pieceW) + '&quot; x ' + fmtDim(cur.pieceH) + '&quot;</text>';
        }
      }

      var header = fmtDim(W) + ' x ' + fmtDim(H) + ' in (' + fmtFtInWords(W) + ' x ' + fmtFtInWords(H) + ')' +
        ' | ' + layout.pieces + (layout.pieces === 1 ? ' piece' : ' pieces') +
        ' | ' + cur.opt.label + ' ' + cur.opt.sheetW + ' x ' + cur.opt.sheetH + ' in' +
        ' | qty ' + this.state.qty;
      var headerXml = String(header).replace(/&/g, '&amp;').replace(/</g, '&lt;');

      return '<?xml version="1.0" encoding="UTF-8"?>\n' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="' + (Math.round(pageW * 1000) / 1000) + 'in" height="' +
             (Math.round(pageH * 1000) / 1000) + 'in" viewBox="0 0 ' + pageW + ' ' + pageH + '">\n' +
        '  <text x="' + pad + '" y="' + (pad * 0.8) + '" font-family="Arial, Helvetica, sans-serif" font-size="0.22" font-weight="bold" fill="#141414">' + headerXml + '</text>\n' +
        '  <text x="' + pad + '" y="' + (pad * 1.15) + '" font-family="Arial, Helvetica, sans-serif" font-size="0.15" fill="#141414">Drawn at 1:12 &#8212; 1 in on this page = 1 ft of stencil. Pieces are equal divisions and align at the seams.</text>\n' +
        '  <g id="TILE-MAP">\n' + body + '\n  </g>\n' +
        '</svg>\n';
    }

    attachTileMap(){
      if (!this.el.tileInput) return;
      var svg;
      try { svg = this.buildTileMapSvg(); } catch (e) { svg = null; }
      if (!svg) { this.el.tileInput.value = ''; return; }
      try {
        var name = (this._dname || 'stencil-order')
          .replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'stencil-order';
        var dt = new DataTransfer();
        dt.items.add(new File([svg], name + '-tile-map.svg', { type: 'image/svg+xml' }));
        this.el.tileInput.files = dt.files;
      } catch (e) {
        this.el.tileInput.value = '';
      }
    }

    toggleSendLater(){
      this.state.sendLater = !this.state.sendLater;
      if (this.state.sendLater) {
        /* Snapshot rather than discard: clicking "undo" a moment later used to
           bring back an empty upload zone instead of the file already chosen,
           because there was nothing left to restore (Colin, 2026-08-12). The
           real file in fileInput is untouched throughout — only the derived
           measurements and the visible state are parked here. */
        if (this.state.artUploaded) {
          this.state.pausedUpload = {
            artW: this.state.artW, artH: this.state.artH,
            fullSrc: this.state.fullSrc, previewable: this.state.previewable,
            savedCrop: this.state.savedCrop,
            fitMode: this.state.fitMode,
            fileName: this._fileName, fileStatus: this._fileStatus,
            croppedThumb: this._croppedThumb
          };
        }
        this.state.artUploaded = false;
        this.state.artW = null; this.state.artH = null;
        this.state.fullSrc = null; this.state.previewable = false;
        this.state.savedCrop = null;
        this.state.fitMode = null;
        this.crop = null;
        if (this.el.croppedInput) this.el.croppedInput.value = '';
        if (this.el.designerInput) this.el.designerInput.value = '';
        if (this.el.tileInput) this.el.tileInput.value = '';
        if (this.el.noPreviewAck) this.el.noPreviewAck.checked = false;
      } else {
        var p = this.state.pausedUpload;
        if (p) {
          this.state.artUploaded = true;
          this.state.artW = p.artW; this.state.artH = p.artH;
          this.state.fullSrc = p.fullSrc; this.state.previewable = p.previewable;
          this.state.savedCrop = p.savedCrop;
          this.state.fitMode = p.fitMode;
          this._fileName = p.fileName; this._fileStatus = p.fileStatus;
          this._croppedThumb = p.croppedThumb;
          this.state.pausedUpload = null;
          this.refreshCroppedUpload();   // rebuild the derived files from the restored source
        }
      }
      this.paintUpload();
      this.refreshGate();
    }

    /* Horizontal chip rows are drag-scrollable with a mouse; the returned
       function reports whether the last gesture was a drag, so a drag doesn't
       also register as a tap on whichever chip it ended over. */
    enableDragScroll(el){
      var isDown = false, startX = 0, scrollLeft = 0, moved = false;
      el.addEventListener('mousedown', function (e) { isDown = true; moved = false; startX = e.pageX; scrollLeft = el.scrollLeft; });
      el.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        var dx = e.pageX - startX;
        if (Math.abs(dx) > 4) moved = true;
        el.scrollLeft = scrollLeft - dx;
      });
      ['mouseup', 'mouseleave'].forEach(function (ev) {
        el.addEventListener(ev, function () { isDown = false; setTimeout(function () { moved = false; }, 0); });
      });
      return function () { return moved; };
    }

    /* ==================================================================
       ADD TO CART — ported from pricer-v2.js apart from the extra
       properties. Read the header of that file before touching any of it.
    ================================================================== */
    async handleAddToCart(){
      if (this.el.atcBtn.disabled) return;
      // Never reached in practice (the button is disabled), but never clamp a flagged size into the cart.
      if (this.sizeError()) { this.jumpTo(STEPS.indexOf('Size')); return; }
      /* Clicking here fires focusout on whichever size field was still focused,
         but that field's finalize is deliberately deferred so it doesn't clamp
         mid-entry. The deferred check hasn't run yet at this point in the same
         synchronous click — so clamp here too, as the last gate before anything
         is priced or submitted. */
      this.state.w = clampDim(this.state.w);
      this.state.h = clampDim(this.state.h);
      this.syncProperties();

      var self = this;
      var settled = false;
      var timer = null;
      /* Guarded and idempotent: the button used to reset ONLY on the theme's
         cart:change / cart:error events, so if neither fired the customer was
         left with a permanently dead "Adding to cart…" button. */
      var resetButton = function () {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        document.removeEventListener('cart:change', resetButton);
        document.removeEventListener('cart:error', resetButton);
        self.el.atcBtn.disabled = false;
        self.el.atcBtn.textContent = ATC_LABEL;
      };

      this.el.atcBtn.disabled = true;
      var hasFile = this.el.fileInput.files && this.el.fileInput.files.length;
      this.el.atcBtn.textContent = hasFile ? 'Uploading artwork…' : 'Adding to cart…';
      this.el.atcNote.textContent = '';

      /* Last-resort release. Better a retry than a button that never comes back. */
      timer = setTimeout(function () {
        if (settled) return;
        resetButton();
        self.el.atcNote.textContent = 'That is taking longer than usual — check your cart before trying again, in case it went through.';
      }, 45000);

      try {
        /* If a crop rebuild is still in flight, wait for it here. This is the one
           gate every path funnels through, so it closes the race regardless of
           which caller started the rebuild. */
        if (this._svgStale) this.refreshCroppedUpload();
        if (this._artworkReady) await this._artworkReady;

        var response = await fetch('/apps/pricer/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            width: this.state.w,
            height: this.state.h,
            qty: this.state.qty,
            /* Says WHICH pricing table to charge from. Large runs 12–360" and
               prices by area; mylar/QR run 2–48" off the base-adder table. The
               backend must not guess from the dimensions, because a 30" square
               is legal in both. Omitting this would silently charge large
               orders at mylar prices. */
            pricer: 'large',
            /* The variant attaches to THIS real product rather than a shared
               internal container, so checkout's bold title is the actual product
               natively. Every generated variant lives on the real product's
               variant list in admin — accepted deliberately (Colin, 2026-08-13). */
            productId: this.dataset.productId
          })
        });
        var result = await response.json();
        if (!response.ok || !result.variantId) {
          throw new Error(result.error || 'Could not get a price for this configuration.');
        }

        var idField = this.form.querySelector('input[name="id"]');
        idField.value = result.variantId.split('/').pop();

        this.el.atcBtn.textContent = hasFile ? 'Uploading artwork…' : 'Adding to cart…';
        /* The backend creates this exact variant via the Admin API moments before
           handing it back. Confirmed live 2026-08-12: Shopify's Storefront cart
           can still fail to find a variant that new with "Cannot find variant"
           even after the backend confirms the SAME variant is readable back
           through the Admin API — Admin and Storefront visibility propagate on
           separate timelines. This retry loop, hitting the real Storefront
           endpoint, is the only place that can observe when it's safe. */
        var retriesLeft = 6;
        var nextDelayMs = 1000;
        var cleanupCartListeners = function () {
          document.removeEventListener('cart:change', onAdded);
          self.form.removeEventListener('cart:error', onAddError);
        };
        var onAdded = function () {
          cleanupCartListeners();
          resetButton();
        };
        var onAddError = function (e) {
          cleanupCartListeners();
          if (settled) return;   // the 45s safety timeout already gave up
          var message = e.detail && e.detail.error;
          if (message === 'Cannot find variant' && retriesLeft > 0) {
            retriesLeft -= 1;
            self.el.atcBtn.textContent = 'Finalizing your price…';
            setTimeout(submitToCart, nextDelayMs);
            nextDelayMs = Math.min(nextDelayMs * 2, 5000);
            return;
          }
          resetButton();
          self.el.atcNote.textContent = 'Couldn’t add that to your cart — please try again.';
        };
        /* requestSubmit() runs the browser's OWN native constraint validation
           across every field in the form before submitting. Without step="any" a
           decimal like 4.25 counts as invalid, the browser silently blocks the
           submit and fires neither cart:change nor cart:error, and the button
           sits on "Uploading artwork…" until the 45s timeout. Caught live
           2026-08-12 — and see the note in renderSize about why the size inputs
           carry no min/max here. */
        var submitToCart = function () {
          document.addEventListener('cart:change', onAdded, { once: true });
          self.form.addEventListener('cart:error', onAddError, { once: true });
          self.form.requestSubmit();
        };
        submitToCart();
      } catch (err) {
        resetButton();
        this.el.atcNote.textContent = 'Couldn’t get a price for this configuration — please try again.';
        console.error('[pricer-lg] quote fetch failed:', err);
      }
    }

    /* An optional field left blank still submits its key, so every order that
       skipped it showed a bare "Project Details" row in admin with nothing after
       it. The cart hides blank properties; admin deliberately shows everything,
       so the only way to keep it clean is not to send them. Dropping the `name`
       keeps the field out of the form post without touching what the customer
       typed, and it is restored the moment they type again. */
    dropBlankOptionalProps(){
      var self = this;
      ['pinfo'].forEach(function (role) {
        var el = self.querySelector('[data-role="' + role + '"]');
        if (!el) return;
        if (el.value && el.value.trim()) {
          if (el.dataset.propName) { el.name = el.dataset.propName; delete el.dataset.propName; }
        } else if (el.name) {
          el.dataset.propName = el.name;
          el.removeAttribute('name');
        }
      });
    }

    /* Writes current state into the hidden line-item-property inputs right
       before the surrounding <product-form> submits. */
    syncProperties(){
      this.dropBlankOptionalProps();
      /* "Inches" is spelled out in the value itself per Colin's requested format
         (2026-08-12) — see line-item.liquid, which strips this suffix back off
         when building the customer-facing cart text so it isn't duplicated. */
      if (this.el.propWidth) this.el.propWidth.value = this.state.w ? fmtDim(this.state.w) + ' Inches' : '';
      if (this.el.propHeight) this.el.propHeight.value = this.state.h ? fmtDim(this.state.h) + ' Inches' : '';

      /* fitMode 'unverified' means the customer ticked the acknowledgement for a
         file we couldn't rasterise, so nothing ever checked its proportions
         against the ordered size. A bare "Uploaded" would let an unchecked file
         look identical to a cropped one on the production floor. */
      if (this.el.propArtStatus) {
        var artStatus = '';
        if (this.state.sendLater) {
          artStatus = 'Send later';
        } else if (this.state.artUploaded) {
          artStatus = this.state.fitMode === 'unverified' ? 'Uploaded (proportions not verified)' : 'Uploaded';
        }
        this.el.propArtStatus.value = artStatus;
      }

      /* The split is a production instruction, not a display detail — it decides
         what gets cut and how it ships, so it goes on the order in words. */
      var cur = this.currentLayout();
      if (this.el.propSheets) {
        this.el.propSheets.value = cur
          ? cur.layout.pieces + (cur.layout.pieces === 1 ? ' piece' : ' pieces') +
            ' · ' + cur.opt.label + ' (' + cur.opt.sheetW + ' × ' + cur.opt.sheetH + ' in usable) · ' + cur.opt.ship
          : '';
      }
      if (this.el.propPieceSize) {
        this.el.propPieceSize.value = cur
          ? fmtDim(cur.pieceW) + ' × ' + fmtDim(cur.pieceH) + ' Inches each'
          : '';
      }

      if (this.el.qty) this.el.qty.value = this.state.qty;
    }
  }

  customElements.define('pricer-lg-app', PricerLargeApp);
})();
